import { registerSW } from "virtual:pwa-register";
import { AudioManager } from "./AudioManager";
import { InputManager } from "./InputManager";
import { Leaderboard, type ScoreEntry } from "./Leaderboard";
import { SaveManager } from "./SaveManager";
import { cleanHandle, profileUrl } from "./handle";
import { CRASH_JOKES, pickTip } from "../data/easterEggs";
import { RunSession, type CueName, type RunSnapshot } from "../engine/RunSession";
import { Autopilot } from "../engine/Autopilot";
import { GameScene } from "../world/GameScene";
import { Assets } from "../world/Assets";
import { resolveTimeOfDay, timeOfDaySwitch, type TimeOfDay } from "../world/night";
import { skinById, skins } from "../world/Roadster";
import { liveProgress, missionSet, missionText, skipCost, skipMission, type MissionTarget } from "../meta/Missions";
import { applyRunToSave, type RunSummary } from "../meta/Progress";
import { applyReward, boardById, boards, consumables, inventoryKey, openBox, rewardText, upgradeCost, upgradeNames, type BoxReward, type Consumable } from "../meta/Shop";
import { ensureWordHunt, nextLetter, seasonProgress, wordHuntDone } from "../meta/Hunts";
import { achievements, statValue } from "../meta/Achievements";
import type { PowerLevels } from "./SaveManager";
import type { InputAction } from "../data/model";
import { endRun as ER, powers as PW, revive as RV } from "../data/tuning";

/** A power's real duration at an upgrade level (the thrusters' table differs: 5 · 9 · 13 · 17 · 21 · 25 s). */
const upgradeSeconds = (kind: string, level: number): number => {
  const table = (PW.seconds as Record<string, readonly number[]>)[kind];
  return table ? table[Math.min(table.length - 1, Math.max(0, level))] : 5 + 5 * level;
};
import { ICON_SPRITE, ico, plain, type IconName } from "../ui/icons";
import { bump, confettiHtml, flyBonus, motionOK, sweepBanner } from "../ui/fx";
import { boardRarity, rarityLabel, skinRarity } from "../ui/rarity";

type AppState = "menu" | "missions" | "shop" | "board" | "settings" | "ready" | "running" | "paused" | "countdown" | "revive" | "results";
type ShopTab = "skins" | "boards" | "upgrades" | "boosters";
type PreRunKey = "headstart" | "booster" | "shield";

/** the backend: off in dev (the API is not reachable from localhost), on in builds unless VITE_API_BASE is "" */
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? (import.meta.env.DEV ? "" : "https://api.brandthebaltics.com");
const PANELS: AppState[] = ["missions", "shop", "board", "settings"];

const POWER_META: Record<string, { icon: IconName; label: string; color: string }> = {
  magnet: { icon: "magnet", label: "MAGNET", color: "#ff4d5e" },
  x2: { icon: "x2", label: "2X", color: "#9945ff" },
  sneakers: { icon: "springs", label: "SPRINGS", color: "#14f195" },
  jetpack: { icon: "thrusters", label: "THRUSTERS", color: "#38b6ff" },
  headstart: { icon: "headstart", label: "HEADSTART", color: "#ff9a2e" }
};
const UPGRADE_ICON: Record<keyof PowerLevels, IconName> = { magnet: "magnet", x2: "x2", sneakers: "springs", jetpack: "thrusters" };
const ITEM_ICON: Record<Consumable, IconName> = { headstart: "headstart", booster: "booster", shield: "shield", board: "board", mysteryBox: "box" };
const REWARD_ICON: Record<BoxReward["kind"], IconName> = { coins: "coin", jackpot: "coin", keys: "key", board: "board", headstart: "headstart", booster: "booster", shield: "shield" };
const MISSION_ICON: Partial<Record<MissionTarget, IconName>> = {
  collectCoins: "coin", coinsWithMagnet: "magnet", coinsWithJetpack: "thrusters", coinsInAir: "coin", coinsWithSneakers: "springs", spendCoins: "cart",
  scorePoints: "trophy", noCoinsScore: "trophy", beatHighScore: "trophy", jump: "springs", hopEth: "springs", roll: "chevron", rollMiddleLane: "chevron",
  dodgeBarriers: "shield", rideTrucks: "road", stayInLane: "road", distance: "road", pickupMagnets: "magnet", pickupJetpacks: "thrusters",
  pickupSneakers: "springs", pickupPogo: "pogo", pickupPowerups: "booster", useBoards: "board", collectKeys: "key", pickupBoxes: "box", buyBoxes: "box",
  escapeCop: "cop", getCaughtFast: "cop", completeWordHunts: "token", useHeadstart: "headstart", useBoosters: "booster", revive: "key"
};

/** Picks the icon a toast deserves from its words (the engine sends plain text). */
function toastIcon(message: string): IconName | null {
  const m = message.toUpperCase();
  if (m.includes("X HANDLE") || m.startsWith("SCORES POST AS")) return "xlogo";
  if (m.includes("SCORE POSTED")) return "trophy";
  if (m.includes("MAGNET")) return "magnet";
  if (m.startsWith("2X")) return "x2";
  if (m.includes("THRUSTERS")) return "thrusters";
  if (m.includes("HEADSTART")) return "headstart";
  if (m.includes("BOOSTER")) return "booster";
  if (m.includes("MEGA HOP")) return "pogo";
  if (m.includes("SPRINGS") || m.includes("DOUBLE HOP")) return "springs";
  if (m.includes("KEY")) return "key";
  if (m.includes("BOX")) return "box";
  if (m.includes("SHIELD")) return "shield";
  if (m.includes("BOARD")) return "board";
  if (m.includes("ELON")) return "cop";
  if (m.includes("SOL")) return "coin";
  if (m.includes("LETTER")) return "token";
  if (m.includes("MISSION") || m.includes("MULTIPLIER")) return "missions";
  if (m.includes("UNLOCKED") || m.includes("LEVEL")) return "up";
  if (m.includes("BACK ON THE ROAD")) return "play";
  return null;
}

/** Board hooks the 3D scene may offer (GameScene.setBoard / boardPreview, being added by the rendering work): used only when present. */
interface SceneBoardHooks {
  setBoard?: (id: string) => void;
  boardPreview?: (id: string) => string;
}

/** The HUD's live elements, looked up once per HUD render. */
interface HudRefs {
  score: HTMLElement; mult: HTMLElement; coins: HTMLElement; keys: HTMLElement; coinPill: HTMLElement; keyPill: HTMLElement; boards: HTMLElement;
  bars: HTMLElement; boardWrap: HTMLElement; boardTimer: HTMLElement; prompt: HTMLElement; promptArrow: HTMLElement; promptText: HTMLElement;
  needle: SVGElement | null; speed: HTMLElement; speedo: HTMLElement; questTitle: HTMLElement; questProgress: HTMLElement; questBar: HTMLElement; questChip: HTMLElement;
}

/**
 * Screens and flow, the original runner's: menu → ready (tap to start) → running ↔ paused (3-2-1 back) → crash →
 * continue? (keys, a shrinking timer) → 3-2-1 → running … → results (count-up) → menu.
 * The 2D layer: chunky outlined buttons, an icon sprite (src/ui/icons.ts), small effects (src/ui/fx.ts).
 */
export class GameApp {
  private save = new SaveManager();
  private audio = new AudioManager(this.save.data.settings.music, this.save.data.settings.sfx);
  private board = new Leaderboard(API_BASE);
  private assets = new Assets();
  private ready = false;
  /** ?night / ?day (screenshots): wins over the saved setting until the player picks a time of day */
  private timeSwitch = timeOfDaySwitch(location.search);
  private shell: HTMLElement;
  private sceneHost: HTMLElement;
  private uiHost: HTMLElement;
  private scene!: GameScene;
  private input: InputManager;
  private session?: RunSession;
  private pilot = new Autopilot();
  private state: AppState = "menu";
  private lastScreen?: AppState;
  private enterTimer?: number;
  private raf = 0;
  private previous = performance.now();
  private accumulator = 0;
  private readonly fixedStep = 1 / 120;
  private toastTimer?: number;
  private gameOverTimer?: number;
  private deferredInstall?: Event & { prompt(): Promise<void>; userChoice: Promise<{ outcome: string }> };
  private resultCommitted = false;
  private resultsSeen = false;
  /** the crash screen's Excel joke, picked once per run end (re-renders keep it) */
  private resultJoke?: string;
  private summary?: RunSummary;
  private shopTab: ShopTab = "skins";
  private previews = new Map<string, string>();
  private boardPreviews = new Map<string, string>();
  private preRun = { headstart: false, booster: false, shield: false };
  private justArmed?: PreRunKey;
  private justBought?: string;
  private boxRewards: string[] = [];
  private runToken?: string;
  private submitted?: { rank: number; players: number; handle: string } | "queued" | "failed" | "busy" | "rejected";
  private submitting = false;
  private boardCache?: { entries: ScoreEntry[]; players: number; at: number };
  private reviveLeft = 0;
  private needle = 0;
  private countdownLeft = 0;
  private countdownAfter: "running" = "running";
  private countShown = "";
  private resultsStart = 0;
  private lastTick = 0;
  private readyListeners: (() => void)[] = [];
  private powerBarKeys = "";
  private hud?: HudRefs;
  private scoreText = "";
  private shownMult?: number;
  private multHoldUntil = 0;
  private lastCoins = 0;
  private lastKeys = 0;
  private lastCoinBump = 0;
  private boardWasOn = false;
  private missionOpen?: number;
  private promptShown = "";
  /** pre-run effects wait for each other (ms before the next one may start) */
  private fxDelay = 0;
  /** the last input was a key (not a tap or a click): only then does a new screen move the focus to its main button */
  private keyboardNav = false;
  /** dev helpers: ?demo = an autopilot steers, ?freeze=<ms> = the run stops updating after that long (screenshots) */
  private readonly demo = new URLSearchParams(location.search).has("demo");
  private readonly freezeAt = Number(new URLSearchParams(location.search).get("freeze") ?? 0);
  private readonly autostart = new URLSearchParams(location.search).has("autostart");
  private readonly startScreen = new URLSearchParams(location.search).get("screen");
  /** `?intro`: the opening scene plays on its own from the menu (screenshots: `?intro&freeze=<ms after the tap>`) */
  private readonly introSwitch = new URLSearchParams(location.search).has("intro");
  /** the run whose opening scene has played (beginRun plays it once per run), and how long it ran (?freeze counts from the tap) */
  private introFor?: RunSession;
  private introMs = 0;

  constructor(private root: HTMLElement) {
    if (import.meta.env.DEV) this.devWallet();
    this.root.innerHTML = `
      <main class="app-shell">
        ${ICON_SPRITE}
        <div id="scene" class="scene-host" aria-label="SOL Run game world"></div>
        <div id="ui" class="ui-host"></div>
        <div id="toast" class="toast" role="status" aria-live="polite"></div>
      </main>`;
    this.shell = this.requireElement(".app-shell");
    this.sceneHost = this.requireElement("#scene");
    this.uiHost = this.requireElement("#ui");
    this.input = new InputManager(this.root, (action) => this.handleAction(action));
    this.bindInstall();
    registerSW({ immediate: true, onNeedReload: () => { if (!this.ready || this.state === "menu" || PANELS.includes(this.state)) location.reload(); } });
    document.addEventListener("visibilitychange", () => { if (document.hidden && this.state === "running") this.pause(); });
    window.addEventListener("blur", () => { if (this.state === "running" && !this.demo) this.pause(); });   // alt-tab, notification shade
    // rotation, browser bars, split screen: the scene sizes itself on window resize; make sure it hears the settled size
    if (typeof ResizeObserver === "function") {
      let queued = 0;
      new ResizeObserver(() => { cancelAnimationFrame(queued); queued = requestAnimationFrame(() => window.dispatchEvent(new Event("resize"))); }).observe(this.sceneHost);
    }
    // iOS leaves the page scrolled after the keyboard closes (name field): put it back
    window.addEventListener("focusout", () => window.setTimeout(() => { if (window.scrollX || window.scrollY) window.scrollTo(0, 0); }, 60));
    // iOS only shows :active press states when a touch listener exists
    document.addEventListener("touchstart", () => undefined, { passive: true });
    window.addEventListener("keydown", (event) => { if (["Tab", "Enter", " ", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) this.keyboardNav = true; }, true);
    window.addEventListener("pointerdown", () => { this.keyboardNav = false; }, true);
    this.uiHost.innerHTML = `<section class="loading-screen"><img src="${import.meta.env.BASE_URL}icons/sol-mark.svg" alt="" width="101" height="88"><strong>SOL RUN</strong><i><b id="load-bar"></b></i><small id="load-note">${this.escape(this.save.data.runs > 0 ? pickTip() || "YOU STOLE ELON'S CAR. HE WANTS IT BACK." : "YOU STOLE ELON'S CAR. HE WANTS IT BACK.")}</small></section>`;
    void this.init();
  }

  /** Loads the models, builds the scene, then shows the menu (or the start line / a screen for the dev switches). */
  private async init(): Promise<void> {
    try {
      await this.assets.load((done, total) => {
        const bar = this.uiHost.querySelector<HTMLElement>("#load-bar");
        if (bar) bar.style.width = `${Math.round((done / total) * 100)}%`;
      });
    } catch (error) {
      this.text("#load-note", "COULDN'T LOAD THE CITY · RELOAD THE PAGE");
      console.error(error);
      return;
    }
    // the bar shows full before the scene build takes the main thread (it jumped from ~90 % straight to the lobby): a short hold
    // at 100 %, on a timer (frames don't run in a hidden or covered window, and the load must never wait for one)
    const bar = this.uiHost.querySelector<HTMLElement>("#load-bar");
    if (bar) bar.style.width = "100%";
    await new Promise<void>((resolve) => window.setTimeout(resolve, 260));   // the bar eases its width over .2 s (main.css)
    this.scene = new GameScene(this.sceneHost, this.assets);
    this.ready = true;
    this.applySettings();
    window.setInterval(() => this.applyTimeOfDay(), 60_000);      // "Auto" turns to night at 20:00 and back at 06:00
    this.applyCar();
    this.renderState();
    this.audio.startMusic("menu");                    // the lobby's song: queued now, heard from the first tap (browsers hold sound until then)
    if (import.meta.env.DEV && new URLSearchParams(location.search).has("clean")) this.uiHost.style.visibility = "hidden";   // dev ?clean: the scene only
    void this.board.flush();
    this.previous = performance.now();
    this.raf = requestAnimationFrame(this.loop);
    if (import.meta.env.DEV && new URLSearchParams(location.search).has("perf")) this.perfProbe();
    if (import.meta.env.DEV && new URLSearchParams(location.search).has("prof")) this.startProf();
    if (new URLSearchParams(location.search).has("play") || this.introSwitch) window.setTimeout(() => this.startRun(), 100);
    else if (this.startScreen) window.setTimeout(() => this.setState(this.startScreen as AppState), 100);
  }

  /** Dev only (never in a build): `?keys=200` / `?coins=50000` set this device's wallet, then leave the address (a reload doesn't redo it). */
  private devWallet(): void {
    const params = new URLSearchParams(location.search);
    const skin = params.get("skin");                                   // dev ?skin=solana|gold|holo|excel…: drive that paint (promo stills)
    if (skin) { this.save.data.selectedCarId = skin; if (!this.save.data.unlockedCars.includes(skin)) this.save.data.unlockedCars.push(skin); this.save.save(); }
    let changed = false;
    for (const field of ["keys", "coins"] as const) {
      const value = params.get(field);
      if (value === null || !/^\d{1,7}$/.test(value)) continue;
      if (field === "keys") this.save.data.keys = Number(value);
      else this.save.data.totalCoins = Number(value);
      params.delete(field);
      changed = true;
    }
    if (!changed) return;
    this.save.save();
    const rest = params.toString();
    history.replaceState(null, "", `${location.pathname}${rest ? `?${rest}` : ""}${location.hash}`);
  }

  /** The X handle the player's scores post under (typed, saved on this device; no sign-in), or undefined. */
  private get handle(): string | undefined {
    return cleanHandle(this.save.data.playerName);
  }

  /** Pause: the siren stops with the run. */
  private pause(): void {
    this.audio.setSiren(false);
    this.setState("paused");
  }

  private requireElement(selector: string): HTMLElement {
    const element = this.root.querySelector<HTMLElement>(selector);
    if (!element) throw new Error(`Missing ${selector}`);
    return element;
  }

  /** dev (?perf): every 5 s logs rAF fps, the GPU string and renderer.info to the console (for headless measures). */
  // ---------------------------------------------------------------------------------------- dev: ?prof frame profiler
  private prof?: { t0: number; update: number; render: number; gpu: number; gpuN: number; frames: number; long: number; maxFrame: number; renderStart: number;
    gl?: WebGL2RenderingContext; ext?: { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number } | null; queries: WebGLQuery[]; active?: WebGLQuery | null; seenPrograms: number };

  /** ?prof: per 5 s — fps, frame time avg/max, frames over 25 ms, game update ms, render CPU ms (three.js submission), GPU ms (timer query). */
  private startProf(): void {
    const r = (this.scene as unknown as { renderer?: { getContext(): WebGL2RenderingContext } }).renderer;
    const gl = r?.getContext();
    const ext = gl?.getExtension("EXT_disjoint_timer_query_webgl2") as { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number } | null | undefined;
    this.prof = { t0: 0, update: 0, render: 0, gpu: 0, gpuN: 0, frames: 0, long: 0, maxFrame: 0, renderStart: 0, gl, ext: ext ?? null, queries: [], seenPrograms: 0 };
    window.setInterval(() => {
      const p = this.prof!;
      const n = Math.max(1, p.frames);
      console.log(`PROF fps=${(p.frames / 5).toFixed(1)} frameMax=${p.maxFrame.toFixed(1)}ms long(>25ms)=${p.long} update=${(p.update / n).toFixed(2)}ms renderCPU=${(p.render / n).toFixed(2)}ms gpu=${p.gpuN ? (p.gpu / p.gpuN).toFixed(2) + "ms" : (p.ext ? "pending" : "no timer ext")} state=${this.state}`);
      p.update = p.render = p.gpu = 0; p.gpuN = 0; p.frames = 0; p.long = 0; p.maxFrame = 0;
      // which shader programs appeared since the last report (each one is a mid-run compile = a hitch)
      const programs = (this.scene as unknown as { renderer?: { info: { programs?: { name: string; cacheKey: string }[] } } }).renderer?.info.programs ?? [];
      if (programs.length > p.seenPrograms) { console.log(`PROF new programs: ${programs.slice(p.seenPrograms).map((q) => q.name + "|" + q.cacheKey.slice(0, 90).replace(/,/g, " ")).join("  ##  ")}`); p.seenPrograms = programs.length; }
    }, 5000);
  }

  private profBeginRender(_now: number, frame: number): void {
    const p = this.prof!;
    p.frames += 1;
    p.maxFrame = Math.max(p.maxFrame, frame * 1000);
    if (frame > 0.025) p.long += 1;
    if (p.gl && p.ext) {
      // collect finished queries (they resolve a few frames later)
      while (p.queries.length && p.gl.getQueryParameter(p.queries[0], p.gl.QUERY_RESULT_AVAILABLE)) {
        const q = p.queries.shift()!;
        if (!p.gl.getParameter(p.ext.GPU_DISJOINT_EXT)) { p.gpu += p.gl.getQueryParameter(q, p.gl.QUERY_RESULT) / 1e6; p.gpuN += 1; }
        p.gl.deleteQuery(q);
      }
      if (p.queries.length < 8) { p.active = p.gl.createQuery(); if (p.active) p.gl.beginQuery(p.ext.TIME_ELAPSED_EXT, p.active); }
    }
    p.renderStart = performance.now();
  }

  private profEndRender(): void {
    const p = this.prof!;
    p.render += performance.now() - p.renderStart;
    if (p.gl && p.ext && p.active) { p.gl.endQuery(p.ext.TIME_ELAPSED_EXT); p.queries.push(p.active); p.active = null; }
  }

  private perfProbe(): void {
    let frames = 0, last = performance.now();
    const count = () => { frames++; requestAnimationFrame(count); };
    requestAnimationFrame(count);
    window.setInterval(() => {
      const now = performance.now(), fps = (frames * 1000) / (now - last);
      frames = 0; last = now;
      const r = (this.scene as unknown as { renderer?: { getContext(): WebGLRenderingContext; info: { render: { calls: number; triangles: number; points: number; lines: number }; memory: { geometries: number; textures: number }; programs?: unknown[] }; getPixelRatio(): number; domElement: HTMLCanvasElement } }).renderer;
      let gpu = "?";
      try { const gl = r?.getContext(); const ext = gl?.getExtension("WEBGL_debug_renderer_info"); gpu = ext && gl ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) : "no ext"; } catch { gpu = "err"; }
      const i = r?.info;
      const line = `PERF fps=${fps.toFixed(1)} state=${this.state} tod=${this.scene?.timeOfDay} gpu=${gpu} dpr=${r?.getPixelRatio()} canvas=${r?.domElement.width}x${r?.domElement.height} calls=${i?.render.calls} tris=${i?.render.triangles} geos=${i?.memory.geometries} tex=${i?.memory.textures} programs=${i?.programs?.length} ents=${this.session?.entities.length ?? 0}`;
      console.log(line);
      // the same line on screen (headless screenshots have no console): ?perf only, dev only
      let tag = document.getElementById("perf-line");
      if (!tag) {
        tag = document.createElement("pre");
        tag.id = "perf-line";
        tag.style.cssText = "position:fixed;left:6px;bottom:6px;z-index:9999;margin:0;padding:3px 6px;max-width:96vw;white-space:pre-wrap;font:11px/1.3 monospace;color:#fff;background:rgba(0,0,0,.65);pointer-events:none";
        document.body.append(tag);
      }
      tag.textContent = line;
    }, 5000);
  }

  // ------------------------------------------------------------------------------------------------ the loop

  private loop = (now: number): void => {
    const frame = Math.min(0.05, (now - this.previous) / 1000);
    this.previous = now;
    let snapshot: RunSnapshot | undefined;                         // built once per frame: the HUD and the scene share it
    if (this.state === "running" && this.session) {
      const frozen = this.freezeAt > 0 && this.session.durationMs() >= this.freezeAt - this.introMs;
      if (this.prof) this.prof.t0 = performance.now();
      if (!frozen) {
        // smooth motion: every frame advances exactly the real time that passed, split into equal sub-steps of at most
        // 1/120 s (collisions keep their small steps). A fixed-step accumulator without interpolation moved the world
        // 1, 2 or 3 steps per frame by timing jitter — visible judder at speed.
        const steps = Math.max(1, Math.ceil(frame / this.fixedStep - 1e-6));
        const dt = frame / steps;
        for (let i = 0; i < steps; i++) {
          if (this.demo) this.pilot.step(this.session, dt);
          this.session.update(dt);
        }
      }
      if (this.prof) this.prof.update += performance.now() - this.prof.t0;
      snapshot = this.session.snapshot();
      this.updateHud(snapshot);
    } else if (this.state === "revive") {
      this.reviveLeft -= frame;
      const bar = this.uiHost.querySelector<HTMLElement>("#revive-bar");
      if (bar) bar.style.transform = `scaleX(${Math.max(0, this.reviveLeft / RV.promptSeconds)})`;
      this.text("#revive-secs", String(Math.max(0, Math.ceil(this.reviveLeft))));
      if (this.reviveLeft <= 0) this.setState("results");
    } else if (this.state === "countdown") {
      const before = Math.ceil(this.countdownLeft);
      this.countdownLeft -= frame * RV.countdownSpeed;
      const n = Math.ceil(this.countdownLeft);
      if (n !== before) this.audio.cue("countdown");
      this.showCount(n > 0 ? String(n) : "GO!");
      if (this.countdownLeft <= -0.4) { this.previous = performance.now(); this.setState(this.countdownAfter); }
    } else if (this.state === "results") {
      this.animateResults((now - this.resultsStart) / 1000);
    }
    const showRun = this.state === "running" || this.state === "paused" || this.state === "ready" || this.state === "countdown" || this.state === "revive" || this.state === "results";
    if (this.prof) this.profBeginRender(now, frame);
    if (showRun && !snapshot) snapshot = this.session?.snapshot();
    this.scene?.render(showRun ? snapshot : undefined, showRun ? this.session?.entities ?? [] : [], frame);
    if (this.prof) this.profEndRender();
    this.raf = requestAnimationFrame(this.loop);
  };

  private setState(state: AppState): void {
    this.clearReadyListeners();
    this.input.reset();
    this.state = state;
    if (state !== "running") this.audio.setSiren(false);
    else (document.activeElement as HTMLElement | null)?.blur?.();   // a focused button must not catch the hop key
    this.uiHost.classList.remove("danger");
    this.scene?.setRunning(state === "running");
    this.renderState();
  }

  private clearReadyListeners(): void {
    for (const off of this.readyListeners) off();
    this.readyListeners = [];
  }

  private renderState(): void {
    const fresh = this.state !== this.lastScreen;
    this.lastScreen = this.state;
    this.uiHost.className = `ui-host state-${this.state}${fresh ? " enter" : ""}${this.save.data.settings.leftHanded ? " left-handed" : ""}`;
    if (fresh) {
      if (this.enterTimer) window.clearTimeout(this.enterTimer);
      this.enterTimer = window.setTimeout(() => this.uiHost.classList.remove("enter"), 700);
    }
    const screens: Record<AppState, () => void> = {
      menu: () => this.renderMenu(), missions: () => this.renderMissions(), shop: () => this.renderShop(), board: () => this.renderBoard(),
      settings: () => this.renderSettings(), ready: () => this.renderReady(), running: () => this.renderHud(), paused: () => this.renderPause(),
      countdown: () => this.renderCountdown(), revive: () => this.renderRevive(), results: () => this.renderResults()
    };
    screens[this.state]();
    this.bindButtons();
    if (fresh && this.keyboardNav) this.uiHost.querySelector<HTMLElement>("[data-autofocus]")?.focus({ preventScroll: true });
  }

  // ------------------------------------------------------------------------------------------------ menu

  private renderMenu(): void {
    const d = this.save.data;
    ensureWordHunt(d.wordHunt);
    const huntDone = wordHuntDone(d.wordHunt);
    const word = d.wordHunt.word.split("").map((ch, i) => {
      const got = i < d.wordHunt.collected || huntDone;
      return `<b class="${got ? "got" : ""}">${got ? this.escape(ch) : ""}</b>`;
    }).join("");
    const season = seasonProgress(d.seasonHunt);
    const boxes = d.inventory.mysteryBoxes + d.inventory.superBoxes;
    const letters = (text: string, from: number) => text.split("").map((ch, i) => `<span class="l" style="--i:${i + from}">${ch}</span>`).join("");
    const rail = (action: string, icon: IconName, label: string, sub: string, cls: string, badge = "") =>
      `<button class="rail-btn ${cls}" data-action="${action}"><span class="rail-tile">${ico(icon)}${badge}</span><span class="rail-text"><span class="rail-label">${label}</span><small>${sub}</small></span></button>`;
    this.uiHost.innerHTML = `
      <section class="menu-screen">
        <header class="menu-top">
          <div class="wallet">
            <div class="pill pill-coin" role="img" aria-label="${this.format(d.totalCoins)} SOL">${ico("coin")}<b>${this.format(d.totalCoins)}</b></div>
            <div class="pill pill-key" role="img" aria-label="${d.keys} keys">${ico("key")}<b>${d.keys}</b></div>
          </div>
          <button class="box-btn${boxes > 0 ? " has" : ""}${d.inventory.superBoxes > 0 ? " super" : ""}" data-action="open-box" aria-label="Open a mystery box (${boxes})">
          <span class="box-tile">${ico("box", d.inventory.superBoxes > 0 ? "super" : "")}</span><b class="badge">${boxes}</b><span class="box-label">${boxes > 0 ? "OPEN" : "BOXES"}</span>
        </button>
          <div class="menu-top-right">
            <button class="tod-btn${this.timeOfDay() === "night" ? " night" : ""}" data-action="time-toggle" aria-label="${this.timeOfDay() === "night" ? "Drive by day" : "Drive at night"}" title="${this.timeOfDay() === "night" ? "Night — tap for day" : "Day — tap for night"}"><span class="tod-tile">${ico(this.timeOfDay() === "night" ? "moon" : "sun")}</span></button>
            <a class="x-btn" href="https://x.com/BalticsCook" target="_blank" rel="noopener noreferrer" aria-label="@BalticsCook on X" title="@BalticsCook on X"><span class="x-tile">${ico("xlogo")}</span></a>
          </div>
        </header>
        <h1 class="lockup" aria-label="SOL Run">
          <img class="lockup-mark" src="${import.meta.env.BASE_URL}icons/sol-mark.svg" alt="" width="101" height="88">
          <span class="lockup-word" aria-hidden="true"><span class="w-sol">${letters("SOL", 0)}</span><span class="w-run">${letters("RUN", 3)}</span></span>
          <span class="lockup-tag" aria-hidden="true">DODGE THE ETH · GRAB THE SOL</span>
        </h1>

        <nav class="menu-rail" aria-label="Game menu">
          ${rail("shop", "cart", "SHOP", `${this.escape(skinById(d.selectedCarId).name)} · ${this.escape(boardById(d.selectedBoardId).name)}`, "r-shop")}
          ${rail("missions", "missions", "MISSIONS", `Set ${d.missions.setIndex + 1} · ${d.missions.done.filter(Boolean).length}/3 done`, "r-missions", `<b class="badge mult">x${d.multiplier}</b>`)}
          ${rail("board", "trophy", "LEADERBOARD", this.handle ? `@${this.escape(this.handle)}` : "Top 100 players", "r-board")}
          ${rail("settings", "gear", "SETTINGS", "Sound · controls", "r-settings")}
        </nav>
        <div class="menu-play">
          <button class="btn btn-green btn-xl play-btn" data-action="play" data-autofocus>${ico("play")}<span>PLAY</span></button>
          <span class="best-chip">${ico("trophy")}<span>BEST <b>${this.format(d.highScore)}</b></span></span>
        </div>
        <aside class="hunt-cards">
          <div class="hunt-card"><span class="hc-label">DAILY WORD${d.wordHunt.streak > 1 ? ` · ${d.wordHunt.streak} DAYS` : ""}</span><strong class="word">${word}</strong><small>${huntDone ? "DONE FOR TODAY" : "LETTERS APPEAR ON THE ROAD"}</small></div>
          <div class="hunt-card season"><span class="hc-label">${ico("token")}SEASON HUNT · TIER ${season.tier}</span><i class="hc-bar"><b style="width:${season.into * 100}%"></b></i><small>${d.seasonHunt.tokens} TOKENS${season.next ? ` · NEXT AT ${season.next}` : ""}</small></div>
        </aside>
        <footer class="menu-footer"><span>BUILT IN THE BALTICS</span><span>${navigator.onLine ? "ONLINE" : "OFFLINE"} · ${d.inventory.boards} BOARDS</span></footer>
      </section>`;
  }

  // ------------------------------------------------------------------------------------------------ the run: ready, HUD

  private renderReady(): void {
    this.clearReadyListeners();
    this.renderHud();
    this.updateHud();
    const first = !this.save.data.tutorialSeen;
    const inv = this.save.data.inventory;
    const touch = typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
    const card = (key: PreRunKey, label: string, sub: string, icon: IconName, count: number) => {
      const on = this.preRun[key];
      const pop = on && this.justArmed === key;
      return `<button class="prerun-card${on ? " on" : ""}${pop ? " pop" : ""}${count <= 0 ? " empty" : ""}" data-action="toggle-${key}" aria-pressed="${on}" aria-label="${label}: ${count} left${on ? ", armed" : ""}" ${count <= 0 ? "disabled" : ""}>
          <span class="pc-icon">${ico(icon)}</span><span class="pc-name">${label}</span><small class="pc-sub">${count <= 0 ? "GET MORE IN THE SHOP" : sub}</small>
          <b class="pc-count">×${count}</b><span class="pc-state">${on ? `${ico("check")}ARMED` : "TAP TO ARM"}</span>${pop ? `<span class="pc-burst"></span>` : ""}
        </button>`;
    };
    this.justArmed = undefined;
    this.uiHost.insertAdjacentHTML("beforeend", `
      <section class="ready-screen" id="ready">
        <strong class="ready-title">TAP TO START</strong>
        <span class="ready-hint">${first ? "WE'LL SHOW YOU THE MOVES ON THE WAY" : touch ? "SWIPE ← → · UP TO HOP · DOWN TO DUCK" : "← → LANES · ↑ HOP · ↓ DUCK · SHIFT BOARD"}</span>
        <div class="prerun-cards" role="group" aria-label="Boosters for this run">
          ${card("headstart", "HEADSTART", "FLY THE FIRST 300 M", "headstart", inv.headstarts)}
          ${card("booster", "SCORE BOOSTER", "+5 TO +7 MULTIPLIER", "booster", inv.boosters)}
          ${card("shield", "SHIELD", "ONE FREE CRASH", "shield", inv.shields)}
        </div>
      </section>`);
    const start = (event: Event): void => {
      if ((event.target as HTMLElement | null)?.closest("button, input, a, [role=button]")) return;
      if (event instanceof KeyboardEvent && (event.key === "Escape" || event.key === "p" || event.key === "P" || event.key === "Tab" || event.key === "Enter")) return;
      this.beginRun();
    };
    this.root.addEventListener("pointerdown", start);
    window.addEventListener("keydown", start);
    this.readyListeners.push(() => this.root.removeEventListener("pointerdown", start), () => window.removeEventListener("keydown", start));
    if (this.demo || this.autostart || this.introSwitch) window.setTimeout(() => this.beginRun(), 50);
  }

  private renderHud(): void {
    const snapshot = this.session?.snapshot();
    this.uiHost.innerHTML = `
      <section class="hud" aria-label="Heads-up display">
        <div class="hud-tl">
          <div class="hud-pill pill-coin" role="img" aria-label="SOL this run">${ico("coin")}<strong id="coins">${snapshot?.coins ?? 0}</strong></div>
          <div class="hud-pill pill-key" role="img" aria-label="Keys this run">${ico("key")}<strong id="keys">${snapshot?.keys ?? 0}</strong></div>
          <div class="speedo" id="speedo" role="img" aria-label="Speedometer">
            <svg viewBox="0 0 120 70" aria-hidden="true">
              <path class="gear g1" d="M14 62 A46 46 0 0 1 32 26" />
              <path class="gear g2" d="M38 21 A46 46 0 0 1 82 21" />
              <path class="gear g3" d="M88 26 A46 46 0 0 1 106 62" />
              <line id="speedo-needle" x1="60" y1="62" x2="60" y2="24" />
              <circle cx="60" cy="62" r="7" />
            </svg>
            <b id="speed">0</b><small>KM/H</small>
          </div>
        </div>
        <div class="hud-tr">
          <div class="hud-score-row">
            <div class="score-block" role="img" aria-label="Score and multiplier">
              <div id="score" class="score-digits" aria-hidden="true"></div>
              <span id="multiplier" class="mult-badge">x${this.shownMult ?? snapshot?.multiplier ?? 1}</span>
            </div>
            <button class="hud-btn pause-btn" data-action="pause" aria-label="Pause">${ico("pause")}</button>
          </div>
          <div id="power-bars" class="power-bars" role="group" aria-label="Power-ups"></div>
        </div>
        <div class="cop-alert" aria-hidden="true">${ico("cop")}<span>ELON!</span></div>
        <div id="tutorial-prompt" class="tutorial-prompt" hidden><span class="prompt-arrow"></span><strong class="prompt-text"></strong></div>
        <div class="hb-wrap" id="board-wrap">
          <div id="board-timer" class="board-ring" hidden></div>
          <button class="board-use" data-action="useboard" aria-label="Use a hover board">${ico("board")}<strong id="boards" class="board-count">${snapshot?.boards ?? 0}</strong></button>
        </div>
        <div class="quest-chip" id="mission-chip">
          <span class="qc-icon">${ico("missions")}</span>
          <span id="mission-title" class="qc-title">MISSION</span>
          <strong id="quest-progress" class="qc-progress"></strong>
          <i class="qc-bar"><b id="quest-bar"></b></i>
        </div>
        <div class="touch-controls" aria-label="Touch controls">
          <button data-input="left" aria-label="Move left">${ico("chevron", "rot-l")}</button>
          <button data-input="jump" aria-label="Hop">${ico("chevron")}</button>
          <button data-input="roll" aria-label="Duck">${ico("chevron", "rot-d")}</button>
          <button data-input="right" aria-label="Move right">${ico("chevron", "rot-r")}</button>
        </div>
      </section>`;
    const q = <T extends Element = HTMLElement>(s: string) => this.uiHost.querySelector(s) as T;
    this.hud = {
      score: q("#score"), mult: q("#multiplier"), coins: q("#coins"), keys: q("#keys"), coinPill: q(".hud .pill-coin"), keyPill: q(".hud .pill-key"),
      boards: q("#boards"), bars: q("#power-bars"), boardWrap: q("#board-wrap"), boardTimer: q("#board-timer"), prompt: q("#tutorial-prompt"),
      promptArrow: q("#tutorial-prompt .prompt-arrow"), promptText: q("#tutorial-prompt .prompt-text"), needle: q<SVGElement>("#speedo-needle"),
      speed: q("#speed"), speedo: q("#speedo"), questTitle: q("#mission-title"), questProgress: q("#quest-progress"), questBar: q("#quest-bar"), questChip: q("#mission-chip")
    };
    this.powerBarKeys = "";
    this.boardP = "";
    this.scoreText = "";
    this.promptShown = "";
    this.lastCoins = snapshot?.coins ?? 0;
    this.lastKeys = snapshot?.keys ?? 0;
    this.boardWasOn = (snapshot?.boardLeft ?? 0) > 0;
  }

  /** HUD writes: a style is set only when its value changed (an unchanged write still costs a style recalc). */
  private readonly powerFills = new Map<string, { fill: HTMLElement; bar: HTMLElement | null }>();
  private boardP = "";
  /** the last number each HUD element shows (updateHud formats only what changed) */
  private hudShown = new WeakMap<Element, number>();
  private readonly styleCache = new WeakMap<Element, Record<string, string>>();
  /** the speedometer's current hue step (only re-styled when it changes) */
  private speedHue = -1;

  private setStyle(el: HTMLElement | SVGElement, prop: "transform", value: string): void {
    let c = this.styleCache.get(el);
    if (!c) { c = {}; this.styleCache.set(el, c); }
    if (c[prop] === value) return;
    c[prop] = value;
    el.style[prop] = value;
  }

  private updateHud(snapshot: RunSnapshot | undefined = this.session?.snapshot()): void {
    const h = this.hud;
    if (!snapshot || !h || !h.score.isConnected) return;
    const now = performance.now();
    const score = Math.floor(snapshot.score);
    if (this.hudShown.get(h.score) !== score) { this.hudShown.set(h.score, score); this.setScore(h.score, this.format(score)); }
    // the multiplier: held while a bonus flies in, then it bumps and glows
    if (this.shownMult === undefined) this.shownMult = this.state === "ready" ? this.save.data.multiplier : snapshot.multiplier;
    if (this.state !== "ready" && snapshot.multiplier !== this.shownMult && now >= this.multHoldUntil) {
      this.shownMult = snapshot.multiplier;
      this.glowMultiplier();
    }
    this.showNumber(h.mult, this.shownMult, "x");
    if (snapshot.coins !== this.lastCoins) {
      if (snapshot.coins > this.lastCoins && now - this.lastCoinBump > 90) { bump(h.coinPill.querySelector(".ico"), this.root, 0.6); this.lastCoinBump = now; }
      this.lastCoins = snapshot.coins;
      this.setText(h.coins, String(snapshot.coins));
    }
    if (snapshot.keys !== this.lastKeys) {
      if (snapshot.keys > this.lastKeys) bump(h.keyPill, this.root, 1);
      this.lastKeys = snapshot.keys;
      this.setText(h.keys, String(snapshot.keys));
    }
    this.showNumber(h.boards, snapshot.boards);
    h.boardWrap.classList.toggle("none", snapshot.boards <= 0 && snapshot.boardLeft <= 0);
    if (this.session) {
      const progress = liveProgress(this.save.data.missions, this.session.metrics, { spentCoins: 0, boughtBoxes: 0, wordHuntsCompleted: 0, highScoreBeaten: false });
      const index = progress.findIndex((p) => !p.done);
      const open = index >= 0 ? progress[index] : undefined;
      if (this.missionOpen !== undefined && index !== this.missionOpen && (index > this.missionOpen || index < 0)) this.missionDone();
      this.missionOpen = index;
      // the mission chip: text and bar rebuilt only when the mission or its count changes
      const shown = open ? (this.save.data.missions.setIndex * 100 + index) * 1e9 + Math.floor(open.value) : -1;
      if (this.hudShown.get(h.questProgress) !== shown) {
        this.hudShown.set(h.questProgress, shown);
        this.setText(h.questTitle, open ? missionText(open.def).toUpperCase() : "ALL MISSIONS DONE");
        this.setText(h.questProgress, open ? `${this.format(open.value)} / ${this.format(open.def.amount)}` : "✓");
      }
      const fill = open ? Math.round(Math.min(1, open.value / open.def.amount) * 1000) : 1000;
      if (this.hudShown.get(h.questBar) !== fill) {
        this.hudShown.set(h.questBar, fill);
        this.setStyle(h.questBar, "transform", `scaleX(${open ? Math.min(1, open.value / open.def.amount).toFixed(3) : 1})`);
      }
    }
    // power-ups: icon + a draining bar each, on the right (no arrays per frame: the keys are walked in place)
    let keys = "";
    for (const k in snapshot.powerups) if (POWER_META[k]) keys = keys ? `${keys},${k}` : k;
    if (keys !== this.powerBarKeys) {
      const bars = Object.entries(snapshot.powerups).filter(([k]) => POWER_META[k]);
      const before = new Set(this.powerBarKeys.split(","));
      h.bars.innerHTML = bars.map(([k]) => `<div class="pbar p-${k}${before.has(k) ? "" : " new"}" style="--pc:${POWER_META[k].color}" role="img" aria-label="${POWER_META[k].label}"><span class="pb-track"><i data-power="${k}"></i></span><span class="pb-icon">${ico(POWER_META[k].icon)}</span></div>`).join("");
      this.powerBarKeys = keys;
      this.powerFills.clear();
      h.bars.querySelectorAll<HTMLElement>("[data-power]").forEach((el) => { this.powerFills.set(el.dataset.power ?? "", { fill: el, bar: el.closest<HTMLElement>(".pbar") }); });
    }
    for (const k in snapshot.powerups) {
      if (!POWER_META[k]) continue;
      const left = snapshot.powerups[k];
      const total = this.session?.powers.active.get(k as never)?.total ?? left;
      const cached = this.powerFills.get(k);
      if (!cached) continue;
      const ratio = Math.max(0, Math.min(1, left / total));
      const q = Math.round(ratio * 1000);
      if (this.hudShown.get(cached.fill) !== q) { this.hudShown.set(cached.fill, q); this.setStyle(cached.fill, "transform", `scaleX(${ratio.toFixed(3)})`); }
      const ending = left < 2.5;
      if (cached.bar && cached.bar.classList.contains("ending") !== ending) cached.bar.classList.toggle("ending", ending);
    }
    // the hover board: a ring that drains around the button; the moment one starts, the button flashes
    const boardOn = snapshot.boardLeft > 0;
    if (h.boardTimer.hidden === boardOn) h.boardTimer.hidden = !boardOn;
    if (boardOn) { const p = Math.max(0, Math.min(1, snapshot.boardTotal > 0 ? snapshot.boardLeft / snapshot.boardTotal : 0)).toFixed(3); if (p !== this.boardP) { this.boardP = p; h.boardTimer.style.setProperty("--p", p); } }
    if (boardOn && !this.boardWasOn) this.flashBoard();
    this.boardWasOn = boardOn;
    // the tutorial prompt
    const prompt = snapshot.tutorialPrompt ?? "";
    if (prompt !== this.promptShown) {
      this.promptShown = prompt;
      h.prompt.hidden = !prompt;
      if (prompt) {
        h.promptArrow.innerHTML = prompt === "jump" ? ico("chevron") : prompt === "roll" ? ico("chevron", "rot-d") : `${ico("chevron", "rot-l")}${ico("chevron", "rot-r")}`;
        h.promptArrow.className = `prompt-arrow ${prompt}`;
        h.promptText.textContent = prompt === "jump" ? "SWIPE UP TO HOP" : prompt === "roll" ? "SWIPE DOWN TO DUCK" : "SWIPE TO CHANGE LANE";
      }
    }
    // the speedometer: three gears light up as the run speeds up; it shakes near the top (the original's speed widget)
    const n = Math.max(0, (snapshot.speed - 22) / 22);
    this.needle += (Math.min(1.15, n) - this.needle) * 0.12;
    const angle = -90 + 180 * Math.min(1.1, this.needle);
    if (h.needle && this.hudShown.get(h.needle) !== Math.round(angle * 10)) { this.hudShown.set(h.needle, Math.round(angle * 10)); this.setStyle(h.needle, "transform", `rotate(${angle.toFixed(1)}deg)`); }
    this.showNumber(h.speed, Math.round(snapshot.speed * 3.6));
    h.speedo.classList.toggle("g1-on", n >= 0.33);
    h.speedo.classList.toggle("g2-on", n >= 0.66);
    h.speedo.classList.toggle("g3-on", n >= 0.95);
    h.speedo.classList.toggle("soft", n >= 0.85 && n < 0.95);
    h.speedo.classList.toggle("hard", n >= 0.95);
    // the number and needle take the colour of the speed: green cruising → yellow → orange → red at the top,
    // Solana violet when thrusters/headstart/boost pads push past it (hue quantized: the style changes only every 4°)
    const hue = n > 1.05 ? 275 : n <= 0 ? 145 : n < 0.5 ? 145 - (145 - 50) * (n / 0.5) : n < 0.85 ? 50 - 22 * ((n - 0.5) / 0.35) : 28 - 28 * Math.min(1, (n - 0.85) / 0.15);
    const step = Math.round(hue / 4) * 4;
    if (this.speedHue !== step) { this.speedHue = step; h.speedo.style.setProperty("--speed-color", `hsl(${step} 88% 44%)`); h.speedo.style.setProperty("--speed-glow", `hsl(${step} 95% 55% / 0.55)`); }
    const hunted = snapshot.hunted && !snapshot.caught;
    if (this.uiHost.classList.contains("danger") !== hunted) {
      this.uiHost.classList.toggle("danger", hunted);
      if (this.state === "running") this.audio.setSiren(hunted);   // paused, counting down, revive: the HUD shows it, the siren stays off
    }
    if (snapshot.tutorialDone && !this.save.data.tutorialSeen) { this.save.data.tutorialSeen = true; this.save.save(); }
  }

  /** The score as one span per character: the hundreds and up hop a little when they change (an odometer feel). */
  private setScore(el: HTMLElement, value: string): void {
    const previous = this.scoreText;
    if (value === previous && el.childElementCount === value.length) return;
    this.scoreText = value;
    if (previous.length !== value.length || el.childElementCount !== value.length) {
      el.innerHTML = [...value].map((ch) => `<span${ch === "," ? ' class="sep"' : ""}>${ch}</span>`).join("");
      if (previous) bump(el, this.root, 0.6);
      return;
    }
    const spans = el.children;
    for (let i = 0; i < value.length; i++) {
      if (value[i] === previous[i]) continue;
      spans[i].textContent = value[i];
      if (value.length - 1 - i >= 2) bump(spans[i], this.root, 0.7);
    }
  }

  private glowMultiplier(): void {
    const badge = this.hud?.mult;
    if (!badge) return;
    bump(badge, this.root, 1.6);
    badge.classList.remove("glow");
    void badge.offsetWidth;
    badge.classList.add("glow");
  }

  /** A score booster or the mystery "+10": the bonus pops in the middle, flies into the multiplier badge, the badge bumps. */
  private multiplierBonus(label: string): void {
    const delay = this.fxDelay;
    this.multHoldUntil = performance.now() + delay + 4000;
    window.setTimeout(() => {
      void flyBonus(this.shell, this.root, label, () => this.uiHost.querySelector("#multiplier")).then(() => {
        this.multHoldUntil = 0;
        this.audio.cue("bump");
      });
    }, delay);
  }

  /** The hover board just started: a green ring flashes around the button and a "HOVER BOARD" pill pops above it. */
  private flashBoard(): void {
    const wrap = this.hud?.boardWrap;
    if (!wrap) return;
    wrap.classList.remove("activated");
    void wrap.offsetWidth;
    wrap.classList.add("activated");
    wrap.querySelector(".board-pill")?.remove();
    wrap.insertAdjacentHTML("beforeend", `<span class="board-pill" aria-hidden="true">${ico("board")}HOVER BOARD</span>`);
    window.setTimeout(() => wrap.querySelector(".board-pill")?.remove(), 1700);
  }

  /** A mission finished mid-run: the chip flashes green. */
  private missionDone(): void {
    const chip = this.hud?.questChip;
    if (!chip) return;
    chip.classList.remove("done-flash");
    void chip.offsetWidth;
    chip.classList.add("done-flash");
    this.audio.cue("quest");
    this.showToast("MISSION COMPLETE");
  }

  private renderPause(): void {
    this.renderHud();
    this.updateHud();
    const live = this.session ? liveProgress(this.save.data.missions, this.session.metrics, { spentCoins: 0, boughtBoxes: 0, wordHuntsCompleted: 0, highScoreBeaten: false }) : undefined;
    this.uiHost.insertAdjacentHTML("beforeend", this.overlay({
      title: "PAUSED", subtitle: "The ETH trucks will wait.", cls: "pause-sheet",
      body: `<h3 class="sub-title">${ico("missions")}MISSIONS · SET ${this.save.data.missions.setIndex + 1}</h3>${this.missionRowsHtml("compact", live)}`,
      foot: `<button class="btn btn-green btn-lg" data-action="resume" data-autofocus>${ico("play")}<span>RESUME</span></button>
        <div class="foot-row"><button class="btn btn-white" data-action="restart">${ico("restart")}<span>RESTART</span></button><button class="btn btn-white" data-action="settings">${ico("gear")}<span>SETTINGS</span></button></div>
        <button class="btn-text" data-action="menu">QUIT TO MENU</button>`
    }));
  }

  private renderCountdown(): void {
    this.renderHud();
    this.updateHud();
    this.countShown = "3";
    this.uiHost.insertAdjacentHTML("beforeend", `<section class="countdown-screen" aria-live="assertive"><strong id="countdown" class="count-num">3</strong></section>`);
  }

  /** 3 · 2 · 1 · GO! — each number pops in when it changes. */
  private showCount(value: string): void {
    if (value === this.countShown) return;
    this.countShown = value;
    const el = this.uiHost.querySelector<HTMLElement>("#countdown");
    if (!el) return;
    el.textContent = value;
    el.classList.toggle("go", value === "GO!");
    if (motionOK(this.root) && typeof el.animate === "function") {
      el.animate([{ transform: "scale(.4) rotate(-8deg)", opacity: 0 }, { transform: "scale(1.15) rotate(3deg)", opacity: 1, offset: 0.55 }, { transform: "scale(1) rotate(0)", opacity: 1 }], { duration: 380, easing: "cubic-bezier(.2,1.4,.4,1)" });
    }
  }

  private renderRevive(): void {
    const cost = this.session?.reviveCost ?? RV.keyCosts[0];
    const caught = !!this.session?.caught;
    this.renderHud();
    this.updateHud();
    this.uiHost.insertAdjacentHTML("beforeend", `
      <section class="revive-screen">
        <div class="revive-card${caught ? " caught" : ""}" role="dialog" aria-modal="true" aria-labelledby="revive-title">
          <span class="revive-badge">${ico(caught ? "cop" : "key")}</span>
          <h2 id="revive-title">${caught ? "CAUGHT!" : "CRASHED!"}</h2>
          <p>${caught ? "Elon wants his car back. Pay in keys?" : "Keep this run going?"}</p>
          <div class="revive-timer" aria-hidden="true"><i id="revive-bar"></i><b id="revive-secs">${Math.ceil(this.reviveLeft || RV.promptSeconds)}</b></div>
          <button class="btn btn-purple btn-lg revive-btn" data-action="revive"><span>CONTINUE</span><span class="cost">${ico("key")}${cost}</span></button>
          <button class="btn-text" data-action="results">NO THANKS</button>
          <small class="have">YOU HAVE ${ico("key")}<b>${this.save.data.keys}</b></small>
        </div>
      </section>`);
  }

  // ------------------------------------------------------------------------------------------------ results

  private renderResults(): void {
    const snapshot = this.session?.snapshot();
    if (!snapshot || !this.session) return;
    if (!this.resultCommitted) {
      const high = this.save.commitRun(snapshot.score, snapshot.coins, snapshot.distance, 0, snapshot.keys);
      this.save.data.inventory.boards = this.session.boards;
      this.save.data.boards = this.session.boards;
      this.summary = applyRunToSave(this.save.data, this.session.metrics, high);
      this.save.save();
      this.resultCommitted = true;
      if (high) window.setTimeout(() => this.audio.cue("high"), 1250);
      if (this.summary.setCompleted) window.setTimeout(() => this.audio.cue("quest"), 1900);
    }
    this.audio.setSiren(false);
    const d = this.save.data;
    const isHigh = snapshot.score >= d.highScore && snapshot.score > 0;
    const caught = snapshot.caught;
    const title = caught ? "CAUGHT!" : "NOT RESPONDING";
    // an Excel joke from easterEggs.ts: the Elon ones when he caught you, the rest after a crash
    this.resultJoke ??= ((jokes) => jokes[Math.floor(Math.random() * jokes.length)])(CRASH_JOKES.filter((j) => /ELON|OWNER/.test(j) === caught));
    const subtitle = isHigh && !caught ? "Microsoft Excel has a new number to chase." : this.resultJoke ?? (caught ? "Elon got his car back." : "Too many ETH trucks are open.");
    const replay = this.resultsSeen;
    this.uiHost.innerHTML = `
      <section class="overlay-screen results-screen ${caught ? "caught" : "crashed"}${isHigh ? " best" : ""}${replay ? " seen" : ""}">
        <div class="sheet results-sheet" role="dialog" aria-modal="true" aria-labelledby="results-title">
          ${caught ? "" : `<div class="win-bar" aria-hidden="true">${ico("grid")}<span>SOL Run (Not Responding)</span><span class="busy"></span><i class="win-btns"><b></b><b></b><b class="x"></b></i></div>`}
          <header class="sheet-head results-head">
            ${caught ? `<span class="head-badge">${ico("cop")}</span>` : ""}
            <h2 id="results-title">${title}</h2>
            <p>${subtitle}</p>
          </header>
          <div class="sheet-body">
            <div class="score-hero">
              ${isHigh ? `<span class="ribbon">NEW BEST!</span>` : ""}
              <small>SCORE</small>
              <strong id="r-score" class="hero-num">0</strong>
              <em id="r-mult" class="mult-badge pop">x${snapshot.multiplier}</em>
            </div>
            <div class="stat-row">
              <div class="stat">${ico("coin")}<b id="r-coins">0</b><small>SOL</small></div>
              <div class="stat">${ico("key")}<b id="r-keys">${snapshot.keys}</b><small>KEYS</small></div>
              <div class="stat">${ico("road")}<b>${this.format(snapshot.distance)}<i>M</i></b><small>DISTANCE</small></div>
            </div>
            <p class="best-line">${ico("trophy")}BEST ${this.format(d.highScore)}</p>
            ${this.missionSummaryHtml()}
            ${this.extrasSummaryHtml()}
            ${this.submitBlockHtml()}
          </div>
          <footer class="sheet-foot">
            <button class="btn btn-green btn-lg" data-action="restart">${ico("restart")}<span>PLAY AGAIN</span></button>
            <div class="foot-row">
              <button class="btn btn-white" data-action="board">${ico("trophy")}<span>LEADERBOARD</span></button>
              <button class="btn btn-white" data-action="menu"><span>CONTINUE</span>${ico("back", "rot-180")}</button>
            </div>
          </footer>
        </div>
      </section>`;
    this.resultsStart = replay ? performance.now() - 60_000 : performance.now();
    this.resultsSeen = true;
    this.lastTick = 0;
  }

  /** The leaderboard part of the results: your X @ + POST SCORE (no sign-in — the board links the @ to the profile), then where you landed. */
  private submitBlockHtml(): string {
    if (!this.board.enabled) return "";
    const sub = this.submitted;
    if (sub === "queued") return `<p class="submit-note">${ico("check")}OFFLINE · YOUR SCORE WILL POST WHEN YOU'RE BACK ONLINE</p>`;
    if (sub === "busy") return `<p class="submit-note">${ico("check")}THE LEADERBOARD IS BUSY · YOUR SCORE WILL POST IN A MOMENT</p>`;
    if (sub === "rejected") return `<p class="submit-note">THIS RUN DIDN'T PASS THE LEADERBOARD'S CHECKS</p>`;
    if (sub === "failed") return `<p class="submit-note">THIS RUN STARTED OFFLINE · ONLY RUNS STARTED ONLINE GO ON THE BOARD</p>`;
    if (sub) return `<p class="submit-note rank">${ico("trophy")}#${this.format(sub.rank)} OF ${this.format(sub.players)} PLAYERS · ${this.handleLink(sub.handle)}</p>`;
    return `<div class="submit-box">
        <form class="submit-row" data-form="submit">
          ${this.handleFieldHtml("player-name", true)}
          <button class="btn btn-purple" type="submit" ${this.submitting ? "disabled" : ""}>${this.submitting ? "POSTING…" : "POST SCORE"}</button>
        </form>
        <p class="handle-note">Your @ goes on the board with a link to your X profile.</p>
      </div>`;
  }

  /** The X handle input: an "@" in front, room to paste a whole profile link (cleaned on submit). */
  private handleFieldHtml(id: string, required: boolean): string {
    return `<label class="handle-field"><span class="at" aria-hidden="true">${ico("xlogo")}@</span><input id="${id}" name="handle" maxlength="64" placeholder="YOUR X HANDLE" value="${this.escape(this.handle ?? "")}" autocomplete="username" autocapitalize="off" autocorrect="off" spellcheck="false" enterkeyhint="send" aria-label="Your X handle for the leaderboard"${required ? " required" : ""}></label>`;
  }

  /** "@handle" linked to the profile on X (the handle is clean: letters, digits, "_"). */
  private handleLink(handle: string): string {
    return `<a class="x-handle" href="${profileUrl(handle)}" target="_blank" rel="noopener noreferrer nofollow ugc">@${this.escape(handle)}</a>`;
  }

  private missionSummaryHtml(): string {
    const summary = this.summary;
    const banner = summary?.setCompleted
      ? `<p class="set-banner">${ico("missions")}MISSION SET DONE · ${summary.newMultiplier ? `MULTIPLIER x${summary.newMultiplier}` : "SUPER MYSTERY BOX"}</p>`
      : summary?.missionsCompleted.length ? `<p class="set-banner small">${ico("check")}DONE: ${summary.missionsCompleted.map((m) => this.escape(m)).join(" · ")}</p>` : "";
    return `${banner}${this.missionRowsHtml("compact")}`;
  }

  /** The three missions of the current set: icon, text, progress bar, count (+ skip buttons on the missions screen). */
  private missionRowsHtml(mode: "panel" | "compact", live?: { value: number; done: boolean }[]): string {
    const d = this.save.data;
    const cost = skipCost(d.missions.setIndex);
    return `<ul class="mission-list ${mode}">${missionSet(d.missions.setIndex).map((def, i) => {
      const done = live ? live[i].done : d.missions.done[i];
      const value = done ? def.amount : Math.min(def.amount, live ? live[i].value : d.missions.progress[i]);
      const skip = mode !== "panel" ? "" : done ? `<em class="m-done">${ico("check")}DONE</em>`
        : `<button class="btn btn-gold btn-sm m-skip" data-skip="${i}" ${d.totalCoins < cost ? "disabled" : ""} aria-label="Skip for ${cost} SOL"><span>SKIP</span>${ico("coin")}<span>${this.format(cost)}</span></button>`;
      return `<li class="${done ? "done" : ""}">
          <span class="m-icon">${ico(done ? "check" : MISSION_ICON[def.target] ?? "missions", !done && (def.target === "roll" || def.target === "rollMiddleLane") ? "rot-d" : "")}</span>
          <span class="m-text">${this.escape(missionText(def))}</span>
          <small class="m-count">${this.format(Math.floor(value))} / ${this.format(def.amount)}</small>
          <i class="m-bar"><b style="width:${(value / def.amount) * 100}%"></b></i>
          ${skip}
        </li>`;
    }).join("")}</ul>`;
  }

  private extrasSummaryHtml(): string {
    const d = this.save.data;
    const s = this.summary;
    const lines: string[] = [];
    if (s?.wordHunt.completed) lines.push(`${ico("token")}<span>WORD HUNT DONE · DAY ${s.wordHunt.streak}${s.wordHunt.reward ? ` · ${this.escape(plain(s.wordHunt.reward))}` : ""}</span>`);
    for (const r of s?.seasonRewards ?? []) lines.push(`${ico("token")}<span>SEASON TIER REACHED · ${this.escape(plain(r))}</span>`);
    for (const u of s?.achievements ?? []) lines.push(`${ico("star")}<span>${this.escape(u.def.name.toUpperCase())} · TIER ${u.tier} · +${u.keys}</span>${ico("key")}`);
    for (const r of this.boxRewards) lines.push(`${ico("box")}<span>BOX: ${this.escape(plain(r))}</span>`);
    const boxes = d.inventory.mysteryBoxes + d.inventory.superBoxes;
    const boxButton = boxes > 0
      ? `<button class="btn btn-orange btn-lg box-open${d.inventory.superBoxes > 0 ? " super" : ""}" data-action="open-box">${ico("box", d.inventory.superBoxes > 0 ? "super" : "")}<span>OPEN ${d.inventory.superBoxes > 0 ? "SUPER " : ""}MYSTERY BOX</span><b class="badge">${boxes}</b></button>`
      : "";
    return `${lines.length ? `<ul class="summary-lines">${lines.map((l) => `<li>${l}</li>`).join("")}</ul>` : ""}${boxButton}`;
  }

  /** The end-run count-up: score first (ticking), then the multiplier pops, then the SOL — the original's timings. */
  private animateResults(t: number): void {
    const snapshot = this.session?.snapshot();
    if (!snapshot) return;
    const scoreTime = Math.min(ER.maxCountSeconds, Math.max(ER.minCountSeconds, (snapshot.score / 1000) * ER.scoreCountPerThousand));
    const coinTime = Math.min(ER.maxCountSeconds, Math.max(ER.minCountSeconds, (snapshot.coins / 100) * ER.coinCountPerHundred));
    const s = Math.min(1, Math.max(0, (t - ER.initialDelay) / scoreTime));
    this.text("#r-score", this.format(snapshot.score * s));
    if (s > 0 && s < 1 && snapshot.score > 0 && t - this.lastTick >= 0.075) { this.lastTick = t; this.audio.cue("ui"); }
    const multAt = ER.initialDelay + scoreTime + ER.multiplierPopDelay;
    const mult = this.uiHost.querySelector<HTMLElement>("#r-mult");
    if (mult && t > multAt && !mult.classList.contains("shown")) {
      mult.classList.add("shown");
      if (t - multAt < 0.5) { this.audio.cue("bump"); bump(this.uiHost.querySelector("#r-score"), this.root, 0.8); }
    }
    const cStart = multAt + 0.2;
    const c = Math.min(1, Math.max(0, (t - cStart) / coinTime));
    this.text("#r-coins", String(Math.floor(snapshot.coins * c)));
    if (c > 0 && c < 1 && Math.floor(t / ER.coinCountupSpacing) % 2 === 0) this.audio.cue("coin");
  }

  // ------------------------------------------------------------------------------------------------ panels

  private renderBoard(): void {
    const cached = this.boardCache && Date.now() - this.boardCache.at < 30_000 ? this.boardCache : undefined;
    const me = this.handle?.toLowerCase();
    const mine = (entry: ScoreEntry) => !!me && entry.handle.toLowerCase() === me;
    const rows = (entries: ScoreEntry[]) => entries.map((entry) => `
      <tr class="${mine(entry) ? "me " : ""}${entry.rank <= 3 ? `top top${entry.rank}` : ""}"><td class="rank"><span>${entry.rank}</span></td><td class="name">${this.handleLink(entry.handle)}</td><td class="num">${this.format(entry.score)}</td><td class="num dist">${this.format(entry.distance)} m</td></tr>`).join("");
    const back = this.session?.ended ? "results" : "menu";
    this.uiHost.innerHTML = this.panel({
      title: "LEADERBOARD", icon: "trophy", back, cls: "board-sheet",
      subtitle: cached ? `${this.format(cached.players)} players · top 100` : "Loading the top 100…",
      body: `${this.handleStripHtml()}
        <div class="board-wrap"><table class="board-table"><thead><tr><th>#</th><th>PLAYER</th><th class="num">SCORE</th><th class="num dist">DISTANCE</th></tr></thead>
        <tbody id="board-rows">${cached ? rows(cached.entries) : ""}</tbody></table></div>
        <p class="submit-note" id="board-note">${cached ? "" : "…"}</p>`,
      foot: `<button class="btn btn-white" data-action="${back}">${ico("back")}<span>BACK</span></button>`
    });
    if (!cached && !this.board.enabled) {
      this.text("#board-note", "THE GLOBAL LEADERBOARD OPENS WHEN SOL RUN GOES LIVE");
      this.text(".panel-titles p", `Your best: ${this.format(this.save.data.highScore)}`);
    } else if (!cached) {
      void this.board.top(100).then((result) => {
        if (this.state !== "board") return;
        const body = this.uiHost.querySelector("#board-rows");
        const note = this.uiHost.querySelector("#board-note");
        if (!result) { if (note) note.textContent = navigator.onLine ? "THE LEADERBOARD IS NOT REACHABLE RIGHT NOW" : "YOU'RE OFFLINE"; return; }
        this.boardCache = { ...result, at: Date.now() };
        if (body) body.innerHTML = rows(result.entries);
        if (note) note.textContent = result.entries.length ? `${this.format(result.players)} PLAYERS` : "NO SCORES YET · BE THE FIRST";
        const sub = this.uiHost.querySelector(".panel-titles p");
        if (sub) sub.textContent = `${this.format(result.players)} players · top 100`;
      });
    }
  }

  /** The board screen: the @ this device's scores post under, editable (empty = cleared); nothing without a backend. */
  private handleStripHtml(): string {
    if (!this.board.enabled) return "";
    return `<form class="account-strip handle-strip" data-form="handle">
        ${this.handleFieldHtml("board-handle", false)}
        <button class="btn btn-white btn-sm" type="submit">SAVE</button>
        <span class="strip-note">Your scores post under this @, linked to your X profile. To take an @ off the board, message <a href="https://x.com/BalticsCook" target="_blank" rel="noopener noreferrer">@BalticsCook</a>.</span>
      </form>`;
  }

  private renderMissions(): void {
    const d = this.save.data;
    const done = d.missions.done.filter(Boolean).length;
    this.uiHost.innerHTML = this.panel({
      title: "MISSIONS", icon: "missions", cls: "missions-sheet",
      subtitle: `Set ${d.missions.setIndex + 1} · finish all three for multiplier x${Math.min(31, d.multiplier + 1)}`,
      body: `<div class="mult-hero"><span class="mult-badge big">x${d.multiplier}</span><div><strong>SCORE MULTIPLIER</strong><small>${done}/3 DONE IN THIS SET · ${d.stats.missionsDone} MISSIONS DONE IN ALL</small></div></div>
        ${this.missionRowsHtml("panel")}
        <h3 class="sub-title">${ico("star")}ACHIEVEMENTS</h3>
        <ul class="achievement-list">${achievements.map((a) => {
          const tier = d.achievements[a.id] ?? 0;
          const value = statValue(this.save.data, a.stat);
          const next = tier < 4 ? a.tiers[tier] : null;
          const stars = Array.from({ length: 4 }, (_, i) => ico("star", i < tier ? "on" : "off")).join("");
          return `<li class="${next ? "" : "complete"}"><span class="a-name">${this.escape(a.name)}</span><span class="a-stars" aria-label="${tier} of 4 stars">${stars}</span><i class="m-bar"><b style="width:${next ? Math.min(100, (value / next) * 100) : 100}%"></b></i><small>${next ? `${this.format(Math.min(value, next))} / ${this.format(next)}` : "COMPLETE"}</small></li>`;
        }).join("")}</ul>`,
      foot: `<button class="btn btn-white" data-action="menu">${ico("back")}<span>BACK</span></button>`
    });
  }

  private renderShop(): void {
    const d = this.save.data;
    const tabs: [ShopTab, string, IconName][] = [["skins", "ROADSTER", "car"], ["boards", "BOARDS", "board"], ["upgrades", "UPGRADES", "up"], ["boosters", "BOOSTERS", "booster"]];
    const bought = (key: string) => this.justBought === key;
    const price = (keys: number | undefined, coins: number, affordable: boolean, attrs: string, label: string) =>
      `<button class="btn ${affordable ? "btn-gold" : "btn-locked"} btn-sm price" ${attrs} ${affordable ? "" : "disabled"} aria-label="${label}">${affordable ? "" : ico("lock", "lock")}${ico(keys ? "key" : "coin")}<span>${this.format(keys ?? coins)}</span></button>`;
    let body = "";
    if (this.shopTab === "boards") {
      body = `<div class="shop-list">${boards.map((b) => {
        const owned = d.unlockedBoards.includes(b.id);
        const selected = d.selectedBoardId === b.id;
        const affordable = b.keys ? d.keys >= b.keys : d.totalCoins >= b.price;
        const rarity = boardRarity(b);
        const button = selected ? `<button class="btn btn-purple btn-sm" data-board="${b.id}">${ico("check")}<span>RIDING</span></button>`
          : owned ? `<button class="btn btn-white btn-sm" data-board="${b.id}">RIDE</button>`
          : price(b.keys, b.price, affordable, `data-board="${b.id}"`, `Buy ${b.name} for ${b.keys ? `${b.keys} keys` : `${b.price} SOL`}`);
        return `<article class="card r-${rarity}${selected ? " equipped" : owned ? " owned" : affordable ? "" : " locked"}${bought(`board:${b.id}`) ? " bought" : ""}">
            ${(() => { const pic = this.boardPreview(b.id); return pic ? `<span class="card-icon preview"><img src="${pic}" alt="${this.escape(b.name)} hover board"></span>` : `<span class="card-icon">${ico("board", "", `--board-color:${b.color}`)}</span>`; })()}
            <div class="card-text"><span class="rarity-tag">${rarityLabel[rarity]}</span><h3>${this.escape(b.name)}</h3><p>${this.escape(b.blurb)}</p>
              <div class="tags">${b.powers.length ? b.powers.map((p) => `<span>${p.replace(/([A-Z])/g, " $1").toUpperCase()}</span>`).join("") : "<span>NO POWERS</span>"}</div></div>
            ${button}${bought(`board:${b.id}`) ? confettiHtml() : ""}
          </article>`;
      }).join("")}</div>`;
    } else if (this.shopTab === "upgrades") {
      body = `<div class="shop-list">${(Object.keys(upgradeNames) as (keyof PowerLevels)[]).map((k) => {
        const level = d.powerLevels[k];
        const cost = upgradeCost(level);
        const pips = Array.from({ length: 6 }, (_, i) => `<b class="${i <= level ? "on" : ""}"></b>`).join("");
        const button = cost ? price(undefined, cost, d.totalCoins >= cost, `data-upgrade="${k}"`, `Upgrade ${upgradeNames[k]} for ${cost} SOL`)
          : `<button class="btn btn-locked btn-sm" data-upgrade="${k}" disabled>MAXED</button>`;
        return `<article class="card${cost ? "" : " maxed"}${bought(`upgrade:${k}`) ? " bought" : ""}">
            <span class="card-icon">${ico(UPGRADE_ICON[k])}</span>
            <div class="card-text"><h3>${upgradeNames[k]}</h3><p>Lasts ${upgradeSeconds(k, level)} s → ${cost ? `${upgradeSeconds(k, level + 1)} s` : "max"}</p><div class="pips" aria-label="Level ${level + 1} of 6">${pips}</div></div>
            ${button}${bought(`upgrade:${k}`) ? confettiHtml() : ""}
          </article>`;
      }).join("")}</div>`;
    } else if (this.shopTab === "boosters") {
      body = `<div class="shop-list">${consumables.map((c) => `<article class="card${bought(`item:${c.id}`) ? " bought" : ""}">
            <span class="card-icon">${ico(ITEM_ICON[c.id])}</span>
            <div class="card-text"><h3>${c.name}</h3><p>${c.blurb}</p><div class="tags"><span class="owned">OWNED ${d.inventory[inventoryKey[c.id]]}</span></div></div>
            ${price(undefined, c.price, d.totalCoins >= c.price, `data-buy-item="${c.id}"`, `Buy ${c.name} for ${c.price} SOL`)}${bought(`item:${c.id}`) ? confettiHtml() : ""}
          </article>`).join("")}</div>`;
    } else {
      body = `<div class="shop-grid">${skins.map((skin) => {
        const unlocked = d.unlockedCars.includes(skin.id) || (skin.price === 0 && !skin.keys);
        const selected = skin.id === d.selectedCarId;
        const affordable = skin.keys ? d.keys >= skin.keys : d.totalCoins >= skin.price;
        const rarity = skinRarity(skin);
        if (!this.previews.has(skin.id) && this.scene) this.previews.set(skin.id, this.scene.skinPreview(skin.id));
        const button = selected ? `<button class="btn btn-purple btn-sm" data-buy="${skin.id}">${ico("check")}<span>DRIVING</span></button>`
          : unlocked ? `<button class="btn btn-white btn-sm" data-buy="${skin.id}">DRIVE</button>`
          : price(skin.keys, skin.price, affordable, `data-buy="${skin.id}"`, `Buy ${skin.name} for ${skin.keys ? `${skin.keys} keys` : `${skin.price} SOL`}`);
        return `<article class="shop-item r-${rarity}${selected ? " equipped" : unlocked ? " owned" : affordable ? "" : " locked"}${bought(`skin:${skin.id}`) ? " bought" : ""}">
            <span class="rarity-tag">${rarityLabel[rarity]}</span>${selected ? `<span class="equipped-tag">${ico("check")}</span>` : ""}
            <div class="item-preview"><img src="${this.previews.get(skin.id) ?? ""}" alt="${this.escape(skin.name)} roadster"></div>
            <h3>${this.escape(skin.name)}</h3>
            ${button}${bought(`skin:${skin.id}`) ? confettiHtml() : ""}
          </article>`;
      }).join("")}</div>`;
    }
    this.justBought = undefined;
    this.uiHost.innerHTML = this.panel({
      title: "SHOP", icon: "cart", cls: "shop-sheet", subtitle: "Boards save a run. Upgrades last longer. Boosters start you strong.",
      extra: `<div class="wallet small"><div class="pill pill-coin" role="img" aria-label="${this.format(d.totalCoins)} SOL">${ico("coin")}<b>${this.format(d.totalCoins)}</b></div><div class="pill pill-key" role="img" aria-label="${d.keys} keys">${ico("key")}<b>${d.keys}</b></div></div>`,
      body: `<div class="tabs" role="tablist" aria-label="Shop sections">${tabs.map(([id, label, icon]) => `<button class="tab${this.shopTab === id ? " active" : ""}" role="tab" aria-selected="${this.shopTab === id}" data-tab="${id}">${ico(icon)}<span>${label}</span></button>`).join("")}</div>${body}`,
      foot: `<button class="btn btn-white" data-action="menu">${ico("back")}<span>BACK</span></button>`
    });
  }

  private renderSettings(): void {
    const s = this.save.data.settings;
    const back = this.session && !this.session.ended ? "pause" : "menu";
    this.uiHost.innerHTML = this.panel({
      title: "SETTINGS", icon: "gear", back, cls: "settings-sheet", subtitle: "Tune the run to how you play.",
      body: `<div class="settings-list">
          ${this.slider("music", "Music", s.music, "music")}
          ${this.slider("sfx", "Sound effects", s.sfx, "speaker")}
          ${this.slider("sensitivity", "Swipe sensitivity", (s.sensitivity - 0.5) / 1.5, "swipe")}
          ${this.toggle("reducedMotion", "Reduced motion", s.reducedMotion, "motion")}
          ${this.toggle("screenShake", "Screen shake", s.screenShake, "shake")}
          ${this.toggle("highContrast", "High-contrast obstacles", s.highContrast, "contrast")}
          <div class="setting-row tod-row" role="radiogroup" aria-label="Time of day"><span class="s-icon">${ico(this.timeOfDay() === "night" ? "moon" : "sun")}</span><span class="s-label">Time of day</span>
            <span class="seg">${(["day", "night", "auto"] as const).map((v) => `<button class="seg-btn${s.timeOfDay === v ? " on" : ""}" role="radio" aria-checked="${s.timeOfDay === v}" data-action="time-${v}" title="${v === "auto" ? "Night from 20:00 to 06:00" : ""}">${v === "day" ? "Day" : v === "night" ? "Night" : "Auto"}</button>`).join("")}</span></div>
          ${this.toggle("leftHanded", "Left-handed controls", s.leftHanded, "hand")}
          ${this.toggle("tapControls", "On-screen buttons", s.tapControls, "gamepad")}
        </div>
        <button class="btn-text danger" data-action="reset-save">RESET SAVE DATA</button>
        <p class="credits">SOL Run by <a href="https://x.com/SolanaOnExcel" target="_blank" rel="noopener">@SolanaOnExcel</a> · a fan game, not affiliated with any game studio ·
          3D city, cars and props: <a href="https://kenney.nl" target="_blank" rel="noopener">Kenney.nl</a> (CC0) ·
          sounds: <a href="https://mixkit.co" target="_blank" rel="noopener">Mixkit</a> (free licence) · music: <a href="https://opengameart.org" target="_blank" rel="noopener">OpenGameArt</a> artists (CC0) ·
          type: Archivo (SIL OFL) · engine started from MetroDash (MIT)</p>`,
      foot: `<button class="btn btn-green" data-action="${back}">${ico("check")}<span>DONE</span></button>`
    });
  }

  private panel(o: { title: string; subtitle?: string; body: string; foot: string; back?: string; extra?: string; cls?: string; icon?: IconName }): string {
    return `<section class="panel-screen"><div class="sheet wide ${o.cls ?? ""}" role="dialog" aria-modal="true" aria-labelledby="panel-title">
        <header class="sheet-head panel-head">
          <button class="hud-btn back-btn" data-action="${o.back ?? "menu"}" aria-label="Back">${ico("back")}</button>
          <div class="panel-titles">${o.icon ? `<span class="title-icon">${ico(o.icon)}</span>` : ""}<h2 id="panel-title">${o.title}</h2>${o.subtitle ? `<p>${o.subtitle}</p>` : ""}</div>
          ${o.extra ? `<div class="panel-extra">${o.extra}</div>` : ""}
        </header>
        <div class="sheet-body">${o.body}</div>
        <footer class="sheet-foot">${o.foot}</footer>
      </div></section>`;
  }

  private overlay(o: { title: string; subtitle: string; body: string; foot: string; cls?: string }): string {
    return `<section class="overlay-screen"><div class="sheet ${o.cls ?? ""}" role="dialog" aria-modal="true" aria-labelledby="overlay-title">
        <header class="sheet-head"><h2 id="overlay-title">${o.title}</h2><p>${o.subtitle}</p></header>
        <div class="sheet-body">${o.body}</div>
        <footer class="sheet-foot">${o.foot}</footer>
      </div></section>`;
  }

  private slider(key: string, label: string, value: number, icon: IconName): string {
    return `<label class="setting-row"><span class="s-icon">${ico(icon)}</span><span class="s-label">${label}</span><input type="range" min="0" max="1" step="0.05" value="${value}" data-setting="${key}" style="--v:${value}"></label>`;
  }

  private toggle(key: string, label: string, checked: boolean, icon: IconName): string {
    return `<label class="setting-row"><span class="s-icon">${ico(icon)}</span><span class="s-label">${label}</span><input type="checkbox" role="switch" ${checked ? "checked" : ""} data-setting="${key}"><i class="switch" aria-hidden="true"></i></label>`;
  }

  // ------------------------------------------------------------------------------------------------ actions

  private bindButtons(): void {
    this.uiHost.querySelectorAll<HTMLElement>("[data-action]").forEach((element) => {
      if (element.dataset.bound) return;
      element.dataset.bound = "1";
      element.addEventListener("click", (event) => { event.stopPropagation(); this.perform(element.dataset.action ?? ""); });
    });
    this.uiHost.querySelectorAll<HTMLElement>("[data-input]").forEach((element) => {
      if (element.dataset.bound) return;
      element.dataset.bound = "1";
      element.addEventListener("pointerdown", (event) => { event.preventDefault(); event.stopPropagation(); this.handleAction(element.dataset.input as InputAction); });
    });
    this.uiHost.querySelectorAll<HTMLInputElement>("[data-setting]").forEach((input) => {
      if (input.dataset.bound) return;
      input.dataset.bound = "1";
      input.addEventListener("input", () => this.updateSetting(input));
    });
    this.uiHost.querySelectorAll<HTMLButtonElement>("[data-skip]").forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = "1";
      button.addEventListener("click", () => {
        const d = this.save.data;
        const cost = skipCost(d.missions.setIndex);
        if (d.totalCoins < cost) return;
        d.totalCoins -= cost;
        d.pendingMeta.spentCoins += cost;
        const setBefore = d.missions.setIndex;
        skipMission(d.missions, Number(button.dataset.skip));
        if (d.missions.setIndex > setBefore) { d.multiplier = Math.min(30, d.multiplier + 1); this.showToast(`MULTIPLIER x${d.multiplier}`); }
        this.save.save();
        this.audio.cue("ui");
        this.renderMissions();
        this.bindButtons();
      });
    });
    this.uiHost.querySelectorAll<HTMLButtonElement>("[data-buy]").forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = "1";
      button.addEventListener("click", () => this.buyOrDrive(button.dataset.buy ?? ""));
    });
    this.uiHost.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = "1";
      button.addEventListener("click", () => { this.shopTab = button.dataset.tab as ShopTab; this.audio.cue("ui"); this.renderShop(); this.bindButtons(); if (this.keyboardNav) this.uiHost.querySelector<HTMLElement>(".tab.active")?.focus({ preventScroll: true }); });
    });
    this.uiHost.querySelectorAll<HTMLButtonElement>("[data-board]").forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = "1";
      button.addEventListener("click", () => this.buyOrRideBoard(button.dataset.board ?? ""));
    });
    this.uiHost.querySelectorAll<HTMLButtonElement>("[data-upgrade]").forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = "1";
      button.addEventListener("click", () => this.buyUpgrade(button.dataset.upgrade as keyof PowerLevels));
    });
    this.uiHost.querySelectorAll<HTMLButtonElement>("[data-buy-item]").forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = "1";
      button.addEventListener("click", () => this.buyItem(button.dataset.buyItem as Consumable));
    });
    const form = this.uiHost.querySelector<HTMLFormElement>("[data-form=submit]");
    if (form && !form.dataset.bound) {
      form.dataset.bound = "1";
      form.addEventListener("submit", (event) => { event.preventDefault(); void this.submitScore(); });
    }
    const handleForm = this.uiHost.querySelector<HTMLFormElement>("[data-form=handle]");
    if (handleForm && !handleForm.dataset.bound) {
      handleForm.dataset.bound = "1";
      handleForm.addEventListener("submit", (event) => { event.preventDefault(); this.saveHandle(); });
    }
  }

  private perform(action: string): void {
    this.audio.unlock();
    this.audio.cue("ui");
    switch (action) {
      case "play":
      case "restart":
        this.audio.prefetchRun();                                     // the run track decodes while the ready screen is up
        this.startRun();
        break;
      case "missions": case "shop": case "settings": case "board": case "results":
        this.setState(action as AppState);
        break;
      case "toggle-headstart": case "toggle-booster": case "toggle-shield": {
        const key = action.replace("toggle-", "") as PreRunKey;
        this.preRun[key] = !this.preRun[key];
        if (this.preRun[key]) { this.justArmed = key; this.audio.cue("power"); }
        this.renderState();
        break;
      }
      case "open-box":
        this.openOneBox();
        break;
      case "time-day": case "time-night": case "time-auto": case "time-toggle": {
        const s = this.save.data.settings;
        s.timeOfDay = action === "time-toggle" ? (this.timeOfDay() === "night" ? "day" : "night") : action.slice(5) as TimeOfDay | "auto";
        this.timeSwitch = undefined;                               // the player's pick wins over ?night / ?day
        this.save.save();
        this.applyTimeOfDay();
        this.renderState();
        break;
      }
      case "menu":
        window.clearTimeout(this.gameOverTimer);
        this.session = undefined;
        this.resultCommitted = false;
        this.audio.startMusic("menu");
        this.setState("menu");
        break;
      case "pause":
        if (this.state === "running") this.pause();
        else if (this.state === "settings" && this.session && !this.session.ended) this.setState("paused");   // settings opened from the pause screen
        break;
      case "resume":
        this.startCountdown();
        break;
      case "revive":
        this.revive();
        break;
      case "useboard":
        this.handleAction("board");
        break;
      case "install":
        void this.install();
        break;
      case "reset-save":
        if (window.confirm("Reset all SOL Run progress and settings?")) {
          this.save.reset();
          this.applySettings();
          this.applyCar();
          this.setState("menu");
        }
        break;
    }
  }

  /** A new run: the car waits at the start line until the tap. */
  private startRun(): void {
    window.clearTimeout(this.gameOverTimer);
    this.resultCommitted = false;
    this.resultsSeen = false;
    this.resultJoke = undefined;
    this.submitted = undefined;
    this.submitting = false;
    this.runToken = undefined;
    if (this.board.enabled) void this.board.start().then((token) => { this.runToken = token; });
    this.pilot = new Autopilot();
    this.session = new RunSession({
      cue: (name) => this.audio.cue(name as CueName),
      toast: (message) => this.showToast(message),
      // the 1.5 s death delay belongs to this run: a restart or a quit inside it must not end the next one (QA-03)
      gameOver: () => {
        const run = this.session;
        window.clearTimeout(this.gameOverTimer);
        this.gameOverTimer = window.setTimeout(() => { if (run && this.session === run && run.ended) this.onGameOver(); }, 1500);
      },
      shake: () => this.scene?.triggerShake()
    }, { boards: this.save.data.inventory.boards, tutorial: !this.save.data.tutorialSeen && !this.demo, waitForTap: true, missionMultiplier: this.save.data.multiplier - 1,
         levels: this.save.data.powerLevels, boardPowers: boardById(this.save.data.selectedBoardId).powers, randomBoards: boards.map((b) => b.powers),
         letters: (() => { const l = nextLetter(this.save.data.wordHunt); return l ? [l] : []; })(), seasonTokens: true });
    this.boxRewards = [];
    this.preRun = { headstart: false, booster: false, shield: false };
    this.accumulator = 0;
    this.shownMult = undefined;
    this.multHoldUntil = 0;
    this.missionOpen = undefined;
    this.boardWasOn = false;
    this.setState("ready");
  }

  private beginRun(): void {
    if (!this.session || this.state !== "ready") return;
    if (this.introFor !== this.session && this.playIntro()) return;          // the opening scene first; it calls back here
    this.audio.unlock();
    this.audio.preload(["coin", "jump", "land", "lane", "duck", "crash", "stumble", "horn", "power", "board", "box", "key", "fly", "bump", "high", "quest", "shield", "nitro"]);
    this.session.start();
    const inv = this.save.data.inventory;
    const use = { headstart: this.preRun.headstart && inv.headstarts > 0, booster: this.preRun.booster && inv.boosters > 0, shield: this.preRun.shield && inv.shields > 0 };
    if (use.headstart) inv.headstarts -= 1;
    if (use.booster) inv.boosters -= 1;
    if (use.shield) inv.shields -= 1;
    if (use.headstart || use.booster || use.shield) this.save.save();
    // the pre-run boosters take turns on screen: the HEADSTART banner, then the booster's "+N×", then "SHIELD UP"
    this.fxDelay = use.headstart ? 1250 : 0;
    // dev ?headstart=1|2|3: the original's three headstart levels (80/110/140 m/s; level 3 lands at the top of the speed ramp) — promo takes
    const level = import.meta.env.DEV ? Math.min(3, Math.max(1, Number(new URLSearchParams(location.search).get("headstart")) || 1)) as 1 | 2 | 3 : 1;
    this.session.applyPreRun({ headstart: use.headstart ? level : 0, booster: use.booster, shield: use.shield });
    const shieldAt = this.fxDelay + (use.booster ? 1300 : 0);
    this.fxDelay = 0;
    // a new run: the next track of the playlist — unless the opening scene already started it (then it plays on)
    this.audio.startMusic("run", this.introFor !== this.session);
    this.previous = performance.now();
    this.setState("running");
    if (use.headstart) sweepBanner(this.shell, this.root, "HEADSTART", ico("headstart"));
    if (use.shield) window.setTimeout(() => { if (this.state === "running") this.showToast("SHIELD UP"); }, shieldAt);
  }

  /**
   * The opening scene (world/intro.ts): you take the car off its charging pad, its owner pulls up shouting, the thrusters
   * fire and the camera swings in behind — then the run starts exactly as it would have. A tap or any key skips it.
   * Not with reduced motion, nor for the autopilot (?demo) or ?autostart unless ?intro asks for it.
   */
  private playIntro(): boolean {
    const session = this.session;
    if (!session || !this.scene) return false;
    if (!this.introSwitch && (this.demo || this.autostart || this.save.data.settings.reducedMotion)) return false;
    this.introFor = session;
    this.introMs = 0;
    this.clearReadyListeners();
    this.uiHost.querySelector("#ready")?.remove();
    this.uiHost.classList.add("intro-on");                                // the HUD waits for the run
    this.audio.unlock();
    this.audio.preload(["bump", "horn", "nitro"]);                   // "siren" plays the horn sample
    this.audio.startMusic("run", true);                              // the run's music starts with the getaway, not after it (decoded in the lobby)
    const shownAt = performance.now();
    const skip = (event: Event): void => {
      if (performance.now() - shownAt < 250) return;                      // the tap that started it
      if (event instanceof KeyboardEvent && ["Shift", "Control", "Alt", "Meta", "Tab"].includes(event.key)) return;
      this.beginRun();                                                    // the scene cuts to its last frame (setRunning)
    };
    this.root.addEventListener("pointerdown", skip);
    window.addEventListener("keydown", skip);
    this.readyListeners.push(() => this.root.removeEventListener("pointerdown", skip), () => window.removeEventListener("keydown", skip));
    this.scene.playIntro({
      done: (ms) => { this.introMs = ms; if (this.session === session) this.beginRun(); },
      cue: (name) => this.audio.cue(name as CueName)
    }, this.freezeAt);
    return true;
  }

  private startCountdown(): void {
    this.countdownLeft = 3;
    this.countdownAfter = "running";
    this.setState("countdown");
  }

  private onGameOver(): void {
    if (!this.session) return;
    // the first-run tutorial can't be failed: a crash while it is still teaching continues for free (no keys, no prompt)
    if (this.session.inTutorial && !this.demo) {
      this.session.revive(true);
      this.audio.startMusic("run");
      this.accumulator = 0;
      this.showToast("TRY AGAIN · THE TUTORIAL IS ON US");
      this.startCountdown();
      return;
    }
    const cost = this.session.reviveCost;
    if (this.session.canRevive && this.save.data.keys >= cost) {
      this.reviveLeft = RV.promptSeconds;
      this.setState("revive");
    } else {
      this.setState("results");
    }
  }

  /** A continue: keys from the save, the run goes on after a 3-2-1. */
  private revive(): void {
    if (!this.session || !this.session.canRevive) return;
    const cost = this.session.reviveCost;
    if (this.save.data.keys < cost) return;
    this.save.data.keys -= cost;
    this.save.save();
    this.session.revive();
    this.audio.startMusic("run");
    this.accumulator = 0;
    this.showToast("BACK ON THE ROAD");
    this.startCountdown();
  }

  /** Results → POST SCORE: the typed @ (or a pasted profile link) is cleaned, remembered on this device and sent with the run. */
  private async submitScore(): Promise<void> {
    const snapshot = this.session?.snapshot();
    if (!snapshot || !this.session || this.submitting) return;
    const input = this.uiHost.querySelector<HTMLInputElement>("#player-name");
    if (!input) return;
    const handle = cleanHandle(input.value);
    if (!handle) { this.showToast("TYPE YOUR X HANDLE · LETTERS, NUMBERS, _"); input.focus(); return; }
    this.save.data.playerName = handle;
    this.save.save();
    this.submitting = true;
    this.uiHost.querySelectorAll<HTMLButtonElement>("[data-form=submit] button").forEach((b) => { b.disabled = true; });
    if (!this.runToken) this.runToken = await this.board.start();
    if (!this.runToken) { this.submitting = false; this.submitted = "failed"; if (this.state === "results") this.renderState(); return; }
    const result = await this.board.submit({ token: this.runToken, handle, score: snapshot.score, distance: snapshot.distance, coins: snapshot.coins, duration_ms: this.session.durationMs() });
    this.submitting = false;
    this.submitted = result === undefined ? "failed" : result;
    this.boardCache = undefined;
    if (result && typeof result === "object") this.audio.cue("high");
    if (this.state === "results") this.renderState();
  }

  /** Leaderboard → SAVE: the @ later scores post under; an empty field clears it. */
  private saveHandle(): void {
    const input = this.uiHost.querySelector<HTMLInputElement>("#board-handle");
    if (!input) return;
    const typed = input.value.trim();
    const handle = cleanHandle(typed);
    if (typed && !handle) { this.showToast("THAT'S NOT AN X HANDLE · LETTERS, NUMBERS, _"); input.focus(); return; }
    this.save.data.playerName = handle ?? "";
    this.save.save();
    this.showToast(handle ? `SCORES POST AS @${handle}` : "X HANDLE CLEARED");
    this.renderState();
  }

  private handleAction(action: InputAction): void {
    if (action === "pause") {
      if (this.state === "running") this.pause();
      else if (this.state === "paused") this.startCountdown();
      else if (PANELS.includes(this.state)) this.perform(this.uiHost.querySelector<HTMLElement>(".back-btn")?.dataset.action ?? "menu");   // Esc closes a panel
      return;
    }
    // Space (or ↑ / W) in the lobby starts a run (Karolis) — keys only: a swipe on the menu never does (a focused button takes its own Space)
    if (this.state === "menu" && action === "jump" && this.keyboardNav) { this.perform("play"); return; }
    if (this.state === "running") this.session?.action(action);
  }

  private updateSetting(input: HTMLInputElement): void {
    const key = input.dataset.setting as keyof typeof this.save.data.settings;
    let value: number | boolean = input.type === "checkbox" ? input.checked : Number(input.value);
    if (input.type === "range") input.style.setProperty("--v", input.value);
    if (key === "sensitivity") value = 0.5 + Number(input.value) * 1.5;
    (this.save.data.settings as unknown as Record<string, number | boolean>)[key] = value;
    this.save.save();
    this.applySettings();
  }

  private applySettings(): void {
    const s = this.save.data.settings;
    this.root.classList.toggle("reduced-motion", s.reducedMotion);
    this.root.classList.toggle("tap-controls-off", !s.tapControls);
    this.input?.setSensitivity(s.sensitivity);
    this.audio.setVolumes(s.music, s.sfx);
    this.scene?.setSettings(s.reducedMotion, s.highContrast, s.screenShake);
    this.applyTimeOfDay();
  }

  /** The look right now: ?night / ?day, else the setting (Auto: night from 20:00 to 06:00 local time). */
  private timeOfDay(): TimeOfDay {
    return this.timeSwitch ?? resolveTimeOfDay(this.save.data.settings.timeOfDay);
  }

  /** Day or night on the scene, live (no reload); the lobby's button follows when Auto turns. */
  private applyTimeOfDay(): void {
    const mode = this.timeOfDay();
    if (!this.scene || this.scene.timeOfDay === mode) return;
    this.scene.setTimeOfDay(mode);
    this.root.classList.toggle("night", mode === "night");
    if (this.state === "menu") this.renderState();
  }

  private applyCar(): void {
    this.scene?.setSkin(skinById(this.save.data.selectedCarId).id);
    const hooks = this.scene as unknown as SceneBoardHooks | undefined;
    if (typeof hooks?.setBoard === "function") hooks.setBoard.call(this.scene, boardById(this.save.data.selectedBoardId).id);
  }

  /** A rendered picture of a hover board for the shop, when the scene can make one (cached). */
  private boardPreview(id: string): string | undefined {
    const hooks = this.scene as unknown as SceneBoardHooks | undefined;
    if (typeof hooks?.boardPreview !== "function") return undefined;
    if (!this.boardPreviews.has(id)) {
      try { this.boardPreviews.set(id, hooks.boardPreview.call(this.scene, id)); } catch { return undefined; }
    }
    return this.boardPreviews.get(id);
  }

  private buyOrRideBoard(id: string): void {
    const d = this.save.data;
    const b = boardById(id);
    if (!d.unlockedBoards.includes(id)) {
      if (b.keys) { if (d.keys < b.keys) return; d.keys -= b.keys; }
      else { if (d.totalCoins < b.price) return; d.totalCoins -= b.price; d.pendingMeta.spentCoins += b.price; }
      d.unlockedBoards.push(id);
      this.justBought = `board:${id}`;
      this.showToast(`${b.name.toUpperCase()} UNLOCKED`);
    }
    d.selectedBoardId = id;
    this.save.save();
    this.applyCar();
    this.audio.cue("power");
    this.renderShop();
    this.bindButtons();
  }

  private buyUpgrade(key: keyof PowerLevels): void {
    const d = this.save.data;
    const cost = upgradeCost(d.powerLevels[key]);
    if (!cost || d.totalCoins < cost) return;
    d.totalCoins -= cost;
    d.pendingMeta.spentCoins += cost;
    d.powerLevels[key] += 1;
    this.save.save();
    this.audio.cue("power");
    this.justBought = `upgrade:${key}`;
    this.showToast(`${upgradeNames[key].toUpperCase()} LEVEL ${d.powerLevels[key] + 1}`);
    this.renderShop();
    this.bindButtons();
  }

  private buyItem(id: Consumable): void {
    const d = this.save.data;
    const item = consumables.find((c) => c.id === id);
    if (!item || d.totalCoins < item.price) return;
    d.totalCoins -= item.price;
    d.pendingMeta.spentCoins += item.price;
    d.inventory[inventoryKey[id]] += 1;
    if (id === "mysteryBox") d.pendingMeta.boughtBoxes += 1;
    if (id === "board") d.boards = d.inventory.boards;
    this.save.save();
    this.audio.cue("box");
    this.justBought = `item:${id}`;
    this.showToast(`${item.name.toUpperCase()} +1`);
    this.renderShop();
    this.bindButtons();
  }

  /** Opens one box (super boxes first): the full-screen reveal — rainbow rays, the box shakes, bursts, the prize pops. */
  private openOneBox(): void {
    const d = this.save.data;
    const kind = d.inventory.superBoxes > 0 ? "super" : d.inventory.mysteryBoxes > 0 ? "mystery" : null;
    if (!kind) { this.showToast("NO BOXES · FIND THEM ON THE ROAD OR BUY IN THE SHOP"); return; }
    if (this.root.querySelector(".box-reveal")) return;
    if (kind === "super") d.inventory.superBoxes -= 1; else d.inventory.mysteryBoxes -= 1;
    const reward = openBox(kind);
    applyReward(d, reward);
    d.stats.boxesOpened += 1;
    this.save.save();
    this.boxRewards.push(rewardText(reward));
    const overlay = document.createElement("section");
    overlay.className = `box-reveal ${kind}${reward.kind === "jackpot" ? " jackpot" : ""}`;
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", kind === "super" ? "Super mystery box" : "Mystery box");
    overlay.tabIndex = -1;
    overlay.innerHTML = `
      <div class="rays"></div>
      <div class="box-stage"><div class="box-big">${ico("box", kind === "super" ? "super" : "")}</div><span class="box-flash"></span></div>
      <div class="prize" aria-live="assertive">
        <span class="prize-glow"></span>
        <b class="prize-icon">${ico(REWARD_ICON[reward.kind])}</b>
        <strong>${this.escape(plain(rewardText(reward)))}</strong>
        <small>${kind === "super" ? "SUPER MYSTERY BOX" : "MYSTERY BOX"}</small>
        ${confettiHtml(24)}
      </div>
      <p class="tap">TAP TO CONTINUE</p>`;
    this.shell.append(overlay);
    overlay.focus({ preventScroll: true });
    this.audio.cue("box");
    window.setTimeout(() => this.audio.cue("box"), 450);
    window.setTimeout(() => { overlay.classList.add("open"); this.audio.cue("high"); }, 1250);
    const close = (): void => {
      if (!overlay.classList.contains("open")) return;
      overlay.remove();
      window.removeEventListener("keydown", onKey, true);
      if (this.state === "results" || this.state === "menu") this.renderState();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Enter" || event.key === " " || event.key === "Escape") { event.preventDefault(); event.stopPropagation(); close(); }
    };
    overlay.addEventListener("pointerdown", close);
    window.addEventListener("keydown", onKey, true);
    window.setTimeout(() => overlay.classList.add("done"), 1700);
  }

  private buyOrDrive(id: string): void {
    const skin = skins.find((candidate) => candidate.id === id);
    if (!skin) return;
    const d = this.save.data;
    if (!d.unlockedCars.includes(id) && !(skin.price === 0 && !skin.keys)) {
      if (skin.keys) { if (d.keys < skin.keys) return; d.keys -= skin.keys; }
      else { if (d.totalCoins < skin.price) return; d.totalCoins -= skin.price; d.pendingMeta.spentCoins += skin.price; }
      d.unlockedCars.push(id);
      this.justBought = `skin:${id}`;
      this.showToast(`${skin.name.toUpperCase()} UNLOCKED`);
    }
    this.save.data.selectedCarId = id;
    this.save.save();
    this.applyCar();
    this.audio.cue("power");
    this.renderShop();
    this.bindButtons();
  }

  /** Toasts: icon + words, popping in. The multiplier bonuses fly into the badge instead; "HOVER BOARD" flashes the board button. */
  private showToast(message: string): void {
    const booster = /^SCORE BOOSTER \+(\d+)$/.exec(message);
    const inRun = !!this.session && (this.state === "ready" || this.state === "running");
    if (booster && inRun) { this.multiplierBonus(`+${booster[1]}×`); return; }
    if (message === "+10 MULTIPLIER" && inRun) { this.multiplierBonus("+10×"); return; }
    if (message === "HOVER BOARD" && inRun) return;
    const toast = this.requireElement("#toast");
    const icon = toastIcon(message);
    toast.innerHTML = `${icon ? ico(icon) : ""}<span>${this.escape(message)}</span>`;
    toast.classList.remove("visible");
    void toast.offsetWidth;
    toast.classList.add("visible");
    if (this.toastTimer) window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => toast.classList.remove("visible"), 2200);
  }

  private text(selector: string, value: string): void {
    const element = this.uiHost.querySelector(selector);
    if (element && element.textContent !== value) element.textContent = value;
  }

  private setText(element: Element, value: string): void {
    if (element.textContent !== value) element.textContent = value;
  }

  /** A HUD number: formatted and written only when it changes (per element, so a rebuilt HUD shows it again). */
  private showNumber(element: Element, value: number, prefix = ""): void {
    if (this.hudShown.get(element) === value) return;
    this.hudShown.set(element, value);
    this.setText(element, prefix ? prefix + value : String(value));
  }

  private format(value: number): string {
    return Math.floor(value).toLocaleString("en-US");
  }

  private escape(value: string): string {
    return value.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char] ?? char));
  }

  private bindInstall(): void {
    window.addEventListener("beforeinstallprompt", (event) => {
      event.preventDefault();
      this.deferredInstall = event as typeof this.deferredInstall;
    });
  }

  private async install(): Promise<void> {
    if (this.deferredInstall) {
      await this.deferredInstall.prompt();
      const choice = await this.deferredInstall.userChoice;
      this.showToast(choice.outcome === "accepted" ? "SOL RUN INSTALLED" : "INSTALL AVAILABLE ANYTIME");
      this.deferredInstall = undefined;
    } else {
      this.showToast("USE YOUR BROWSER MENU · ADD TO HOME SCREEN");
    }
  }
}
